import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Snackbar } from 'Components';
import CloudUploadModal from 'Components/UploadDropdown/CloudUploadModal';
import { Pathname } from 'Routes';
import { baseUrl, getToken, axios as apiAxios } from 'Api';
import { generateS3Url } from 'Api/awsConfig';
import { getVideosDetail, getGiftPendingActions, clearGiftActions, addGiftAction, getGiftStatus, createUploadSSESession, createShortLink } from 'Api/apiFunctions';
import { uploadLargeFile } from 'Utils/uploadWrapper';
import uploadManagerSecure from 'Utils/uploadManagerSecure';
import QRCodeComponent from 'Utils/QRCodeComponent';
import { updateDraftLink, createGiftUploadSession, uploadPartWithPresignedUrl, completeGiftUploadSession } from 'Api/uploaderApiFunctions';
import { getUploaderToken } from 'Api/tokenManager';
import image01 from 'Assets/birthday.png';
import image04 from 'Assets/noThumbImage.png';
import { PersonalVideoMessageFlow } from 'Components/PersonalVideoMessageFlow';
import NetflixPlayer from 'Components/NetflixPlayer';
import GiftMessagePlayer from './GiftMessagePlayer';
import { findGiftMessageClips } from './giftMessageMatcher';
import { VIDEO_RECORDER_ENABLED } from '../../../Config/featureFlags';
import styles from './index.module.css';

// Upload constants
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.wmv', '.flv', '.webm', '.m4v', '.3gp', '.mpg', '.mpeg', '.ts', '.mts'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif', '.tiff', '.tif', '.svg'];
const FILE_ACCEPT = `${[...VIDEO_EXTENSIONS, ...IMAGE_EXTENSIONS].join(',')},video/*,image/*`;

// Mirror gift-flow: hide "Upload From Mobile" and "Apple Photos" options on iOS web,
// since the Photos picker on iOS already covers both cases via the native gallery.
const isIOSWeb = typeof navigator !== 'undefined'
  && /iPad|iPhone|iPod/.test(navigator.userAgent || navigator.vendor || '');

const UPLOAD_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  PAUSED: 'paused',
  NETWORK_ERROR: 'network_error',
  COMPLETED: 'completed',
  S3_COMPLETE: 's3_complete',
  FAILED: 'failed',
  CANCELLING: 'cancelling',
  CANCELLED: 'cancelled',
};

const changeSize = (size) => {
  const newSize = size / 1024 / 1024;
  return newSize.toFixed(2);
};

const formatSpeed = (bytesPerSecond) => {
  if (!bytesPerSecond || bytesPerSecond <= 0) return '';
  if (bytesPerSecond > 1024 * 1024) return `${(bytesPerSecond / 1024 / 1024).toFixed(1)} MB/s`;
  if (bytesPerSecond > 1024) return `${(bytesPerSecond / 1024).toFixed(0)} KB/s`;
  return `${bytesPerSecond.toFixed(0)} B/s`;
};

const getStatusLabel = (status) => {
  switch (status) {
    case UPLOAD_STATUS.PENDING: return 'Pending';
    case UPLOAD_STATUS.UPLOADING: return 'Uploading';
    case UPLOAD_STATUS.PAUSED: return 'Paused';
    case UPLOAD_STATUS.NETWORK_ERROR: return 'Network Error';
    case UPLOAD_STATUS.COMPLETED: return 'Completed';
    case UPLOAD_STATUS.S3_COMPLETE: return 'Completing...';
    case UPLOAD_STATUS.FAILED: return 'Failed';
    case UPLOAD_STATUS.CANCELLING: return 'Cancelling...';
    case UPLOAD_STATUS.CANCELLED: return 'Cancelled';
    default: return status;
  }
};

// Convert base64 data URL to Blob (same as gift-flow base64ToBinary)
const base64ToBinary = (base64String) => {
  const [, base64Data] = base64String.split(',');
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const binaryData = new Uint8Array(len);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < len; i++) {
    binaryData[i] = binaryString.charCodeAt(i);
  }
  return new Blob([binaryData], { type: 'image/jpeg' });
};

// Backend fallback: fetch first 5MB of video from S3 and send to backend for thumbnail generation
const callBackendThumbnailFallback = async (videoUrl) => {
  console.log('[THUMB_BACKEND] Fetching first 5MB from:', videoUrl);
  try {
    const resp = await fetch(videoUrl, { headers: { Range: 'bytes=0-5242879' } });
    if (!resp.ok && resp.status !== 206) {
      console.error('[THUMB_BACKEND] S3 fetch failed:', resp.status);
      return null;
    }
    const videoBlob = await resp.blob();
    console.log('[THUMB_BACKEND] Got', videoBlob.size, 'bytes, sending to backend API');
    const formData = new FormData();
    formData.append('video_file', videoBlob);
    const backendResp = await fetch('https://uploader.projector.app/generate-thumbnail/', {
      method: 'POST',
      body: formData,
    });
    if (!backendResp.ok) {
      console.error('[THUMB_BACKEND] Backend API failed:', backendResp.status);
      return null;
    }
    const data = await backendResp.json();
    if (data?.thumbnail) {
      const thumbUrl = `data:image/png;base64,${data.thumbnail}`;
      console.log('[THUMB_BACKEND] Success — got thumbnail from backend for:', videoUrl);
      return thumbUrl;
    }
    console.warn('[THUMB_BACKEND] Backend returned no thumbnail for:', videoUrl);
    return null;
  } catch (err) {
    console.error('[THUMB_BACKEND] Error:', err);
    return null;
  }
};

// Fast single-frame thumbnail for display only
const generateSingleThumbnail = (videoUrl) => new Promise((resolve) => {
  if (!videoUrl) { console.warn('[SINGLE_THUMB] No videoUrl'); resolve(image04); return; }
  console.log('[SINGLE_THUMB] Starting for:', videoUrl);
  let resolved = false;
  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.preload = 'auto';
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  // Safari produces black frames when drawImage'ing from a detached <video>
  // element. Mount offscreen on the DOM so Safari exposes pixels to canvas.
  video.style.cssText = 'position:fixed;left:-99999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
  document.body.appendChild(video);
  const cleanup = () => { try { video.remove(); } catch (e) { /* ignore */ } };
  const timeout = setTimeout(() => { if (!resolved) { console.warn('[SINGLE_THUMB] Timeout for:', videoUrl); resolved = true; cleanup(); resolve(image04); } }, 20000);
  video.onerror = (e) => { if (!resolved) { console.error('[SINGLE_THUMB] Load error for:', videoUrl, e.type); resolved = true; clearTimeout(timeout); cleanup(); resolve(image04); } };
  // Same WebM-no-duration fix as generateThumbnailsFromUrl below.
  const fixDurationIfNeeded = () => new Promise((resolveFix) => {
    if (Number.isFinite(video.duration)) {
      resolveFix(video.duration);
      return;
    }
    const handler = () => {
      if (Number.isFinite(video.duration)) {
        video.removeEventListener('timeupdate', handler);
        try { video.currentTime = 0; } catch (e) { /* noop */ }
        resolveFix(video.duration);
      }
    };
    video.addEventListener('timeupdate', handler);
    try { video.currentTime = 1e6; } catch (e) {
      video.removeEventListener('timeupdate', handler);
      resolveFix(0);
    }
  });
  video.onloadedmetadata = async () => {
    if (resolved) return;
    let { duration } = video;
    const { videoHeight, videoWidth } = video;
    if (!Number.isFinite(duration)) {
      duration = await fixDurationIfNeeded();
      if (resolved) return;
    }
    console.log('[SINGLE_THUMB] Metadata:', videoUrl, 'duration:', duration, 'size:', videoWidth, 'x', videoHeight);
    if (videoWidth === 0 || videoHeight === 0 || !Number.isFinite(duration) || duration <= 0) {
      console.warn('[SINGLE_THUMB] Zero dimensions / invalid duration:', videoUrl);
      resolved = true; clearTimeout(timeout); cleanup(); resolve(image04);
      return;
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const scale = Math.min(1, 640 / videoWidth);
    canvas.width = videoWidth * scale;
    canvas.height = videoHeight * scale;
    // Seek to 1 second for faster seeking (close to start = faster)
    const seekTime = Math.min(1, duration - 0.1);
    let frameTimer = null;
    let frameDrawn = false;
    const drawFrame = () => {
      if (resolved || frameDrawn) return;
      frameDrawn = true;
      if (frameTimer) { clearTimeout(frameTimer); frameTimer = null; }
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumb = canvas.toDataURL('image/jpeg', 0.7);
        console.log('[SINGLE_THUMB] Success for:', videoUrl);
        resolved = true; clearTimeout(timeout); canvas.remove(); cleanup();
        resolve(thumb);
      } catch (e) {
        console.error('[SINGLE_THUMB] Canvas error for:', videoUrl, e.message);
        resolved = true; clearTimeout(timeout); canvas.remove(); cleanup();
        resolve(image04);
      }
    };
    video.onseeked = () => {
      if (resolved) return;
      console.log('[SINGLE_THUMB] Seeked, drawing frame for:', videoUrl);
      if (frameTimer) clearTimeout(frameTimer);
      frameTimer = setTimeout(drawFrame, 500);
      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(() => drawFrame());
      } else {
        requestAnimationFrame(() => requestAnimationFrame(drawFrame));
      }
    };
    // Safari's hardware decoder won't expose decoded frames to canvas until the
    // video has been "played" once. A muted play()/pause() warmup primes it.
    const playAttempt = video.play();
    if (playAttempt && typeof playAttempt.then === 'function') {
      playAttempt
        .then(() => { try { video.pause(); } catch (e) { /* ignore */ } video.currentTime = seekTime; })
        .catch(() => { video.currentTime = seekTime; });
    } else {
      video.currentTime = seekTime;
    }
  };
  video.src = videoUrl;
});

// Generate 6 thumbnails from a video URL (same as gift-flow generateThumbnailsFromVideo)
// Returns { displayThumb, thumbnails[] } where thumbnails are base64 strings
const generateThumbnailsFromUrl = (videoUrl) => new Promise((resolve) => {
  const fallback = { displayThumb: image04, thumbnails: [] };
  if (!videoUrl) { console.warn('[THUMB_GEN] No videoUrl provided, using fallback'); resolve(fallback); return; }
  console.log('[THUMB_GEN] Starting thumbnail generation for:', videoUrl);

  let resolved = false;
  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.preload = 'auto';
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  // Safari produces black frames when drawImage'ing from a detached <video>
  // element (hardware-decoded frames aren't exposed to canvas). Mounting the
  // element offscreen on the DOM lets Safari expose pixels to canvas.
  video.style.cssText = 'position:fixed;left:-99999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
  document.body.appendChild(video);

  const cleanup = () => { try { video.remove(); } catch (e) { /* ignore */ } };

  const timeout = setTimeout(() => {
    if (!resolved) { console.warn('[THUMB_GEN] Timeout (15s) for:', videoUrl); resolved = true; cleanup(); resolve(fallback); }
  }, 15000);

  video.onerror = (e) => {
    if (!resolved) { console.error('[THUMB_GEN] Video load error for:', videoUrl, 'error:', e); resolved = true; clearTimeout(timeout); cleanup(); resolve(fallback); }
  };

  // MediaRecorder-generated WebM blobs ship without a duration in the EBML
  // header → video.duration is `Infinity`, which crashes any currentTime seek.
  // Fix: seek to a huge value once; the browser computes the real duration
  // as a side effect, then we resume the metadata flow.
  const fixDurationIfNeeded = () => new Promise((resolveFix) => {
    if (Number.isFinite(video.duration)) {
      resolveFix(video.duration);
      return;
    }
    const handler = () => {
      if (Number.isFinite(video.duration)) {
        video.removeEventListener('timeupdate', handler);
        try { video.currentTime = 0; } catch (e) { /* noop */ }
        resolveFix(video.duration);
      }
    };
    video.addEventListener('timeupdate', handler);
    try { video.currentTime = 1e6; } catch (e) {
      video.removeEventListener('timeupdate', handler);
      resolveFix(0);
    }
  });

  video.onloadedmetadata = async () => {
    if (resolved) return;
    let { duration } = video;
    const { videoHeight, videoWidth } = video;
    if (!Number.isFinite(duration)) {
      duration = await fixDurationIfNeeded();
      if (resolved) return;
    }
    console.log('[THUMB_GEN] Metadata loaded:', videoUrl, 'duration:', duration, 'size:', videoWidth, 'x', videoHeight);
    if (videoWidth === 0 || videoHeight === 0 || !Number.isFinite(duration) || duration <= 0) {
      console.warn('[THUMB_GEN] Zero dimensions / invalid duration for:', videoUrl);
      resolved = true; clearTimeout(timeout); cleanup(); resolve(fallback); return;
    }

    const thumbnailCount = 6;
    const interval = duration / (thumbnailCount + 1);
    const tempThumbnails = [];

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const maxWidth = 640;
    const scale = Math.min(1, maxWidth / videoWidth);
    canvas.width = videoWidth * scale;
    canvas.height = videoHeight * scale;

    let currentIndex = 0;
    let frameTimer = null;
    let frameDrawn = false;

    const captureThumbnail = () => {
      if (resolved) return;
      if (currentIndex >= thumbnailCount) {
        resolved = true;
        clearTimeout(timeout);
        if (frameTimer) clearTimeout(frameTimer);
        canvas.remove();
        cleanup();
        console.log('[THUMB_GEN] Completed:', videoUrl, '— captured', tempThumbnails.length, 'frames');
        resolve({
          displayThumb: tempThumbnails.length > 0 ? tempThumbnails[0] : image04,
          thumbnails: tempThumbnails,
        });
        return;
      }
      const time = Math.max(0.5, interval * (currentIndex + 1));
      frameDrawn = false;
      video.currentTime = Math.min(time, duration - 0.1);
    };

    const drawCurrentFrame = () => {
      if (resolved || frameDrawn) return;
      frameDrawn = true;
      if (frameTimer) { clearTimeout(frameTimer); frameTimer = null; }
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        tempThumbnails.push(canvas.toDataURL('image/jpeg', 0.7));
        console.log('[THUMB_GEN] Captured frame', currentIndex + 1, 'for:', videoUrl);
      } catch (e) {
        // CORS error — stop capturing
        console.error('[THUMB_GEN] Canvas draw/toDataURL CORS error for:', videoUrl, e.message);
        resolved = true;
        clearTimeout(timeout);
        if (frameTimer) clearTimeout(frameTimer);
        canvas.remove();
        cleanup();
        resolve({
          displayThumb: tempThumbnails.length > 0 ? tempThumbnails[0] : image04,
          thumbnails: tempThumbnails,
        });
        return;
      }
      currentIndex += 1;
      captureThumbnail();
    };

    video.onseeked = () => {
      if (resolved) return;
      // Per-frame fallback: if the frame callback / RAF path doesn't fire
      // (paused-video edge cases on Safari), draw after a short delay so we
      // never stall the whole sequence on the 15s overall timeout.
      if (frameTimer) clearTimeout(frameTimer);
      frameTimer = setTimeout(drawCurrentFrame, 500);

      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(() => drawCurrentFrame());
      } else {
        requestAnimationFrame(() => requestAnimationFrame(drawCurrentFrame));
      }
    };

    // Safari's hardware decoder won't expose decoded frames to canvas until the
    // video has been "played" once. A muted play()/pause() warmup primes the
    // decoder so subsequent seeks produce real (not black) frames.
    const startCapture = () => {
      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.then === 'function') {
        playAttempt
          .then(() => { try { video.pause(); } catch (e) { /* ignore */ } captureThumbnail(); })
          .catch(() => { captureThumbnail(); });
      } else {
        captureThumbnail();
      }
    };

    startCapture();
  };

  video.src = videoUrl;
  video.load();
});

export const GiftContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isExistingUser = localStorage.getItem('existingUser') === 'true';
  const giftUid = localStorage.getItem('giftUid') || '';

 const getAlbumGridClass = (count) => {
    if (count <= 4) return `${styles.albumFolderGrid} ${styles.albumFolderGrid2x2}`;
    if (count <= 6) return `${styles.albumFolderGrid} ${styles.albumFolderGrid3x2}`;
    return styles.albumFolderGrid;
  };
  const [receiverName, setReceiverName] = useState(() => {
    const fn = localStorage.getItem('firstname') || '';
    const ln = localStorage.getItem('lastname') || '';
    return [fn, ln].filter(Boolean).join(' ') || 'Your';
  });

  // Sender name was passed in the /setpassword URL as fromName and persisted
  // to localStorage there. Falls back to the URL param if we ever land here
  // with the param still present, then to a generic label.
  const giftFromName = (
    searchParams.get('fromName')
    || localStorage.getItem('giftFromName')
    || 'Someone special'
  );

  // Fetch profile to get receiver's actual name (new-user flow only — for existing user
  // the gift recipient name comes from getGiftStatus, since getMyProfile would resolve to
  // whoever is currently logged in, which may not be the gift recipient).
  useEffect(() => {
    if (!getToken()) return;
    const isExisting = localStorage.getItem('existingUser') === 'true';
    if (isExisting) return;
    apiAxios.post(`${baseUrl}/getMyProfile`).then((res) => {
      if (res?.data?.success && res?.data?.data) {
        const { firstname, lastname } = res.data.data;
        const fullName = [firstname || '', lastname || ''].filter(Boolean).join(' ');
        if (fullName) {
          setReceiverName(fullName);
          console.log('[GIFT_CONTENT] Profile fetched — receiver name:', fullName);
        }
      }
    }).catch((err) => {
      console.warn('[GIFT_CONTENT] Failed to fetch profile:', err);
    });
  }, []);

  // Existing-user flow: receiver name must come from the gift recipient on the gift record,
  // not from the logged-in user's profile. Fetch getGiftStatus and seed receiverName from
  // its firstname/lastname so pending album titles and video category_name are correct.
  useEffect(() => {
    const isExisting = localStorage.getItem('existingUser') === 'true';
    const uid = localStorage.getItem('giftUid') || '';
    if (!isExisting || !uid) return undefined;
    let cancelled = false;
    getGiftStatus(uid).then((response) => {
      if (cancelled) return;
      const data = response?.data?.data;
      if (!data) return;
      const fullName = [data.firstname || '', data.lastname || ''].filter(Boolean).join(' ').trim();
      if (fullName) {
        setReceiverName(fullName);
        console.log('[GIFT_CONTENT] Existing user — receiver name from gift status:', fullName);
      }
    }).catch((err) => {
      console.warn('[GIFT_CONTENT] Failed to fetch gift status for receiver name:', err);
    });
    return () => { cancelled = true; };
  }, []);

  // Extract and store tokens from URL params (same as gift-flow)
  useEffect(() => {
    const accessParam = searchParams.get('access');
    const tokenParam = searchParams.get('token');

    if (accessParam) {
      localStorage.setItem('uploader_token', accessParam);
      try {
        const payload = JSON.parse(atob(accessParam.split('.')[1]));
        if (payload.exp) {
          localStorage.setItem('uploader_token_expiry', (payload.exp * 1000).toString());
        }
      } catch (e) { /* ignore */ }
      console.log('[GIFT_CONTENT] Uploader token extracted from URL');
    }
    if (tokenParam) {
      localStorage.setItem('token', tokenParam);
      console.log('[GIFT_CONTENT] Token extracted from URL');
    }
    // Pre-fetch uploader token if we have a regular token but no access param
    if (getToken() && !accessParam) {
      getUploaderToken().catch((err) => console.warn('[GIFT_CONTENT] Failed to pre-fetch uploader token:', err));
    }
  }, [searchParams]);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ isVisible: false, message: '', type: 'success' });

  // Cloud connect modal state (mirrors gift-flow's handleCloudConnect)
  const [cloudModalOpen, setCloudModalOpen] = useState(false);
  const [selectedCloudService, setSelectedCloudService] = useState(null);

  // Mobile upload QR popup state (mirrors QuickUserUploadPage)
  const [showMobileQr, setShowMobileQr] = useState(false);
  const [mobileSessionId, setMobileSessionId] = useState(null);
  const [mobileQrLoading, setMobileQrLoading] = useState(false);
  const [mobileQrError, setMobileQrError] = useState(null);
  const [mobileShortUrl, setMobileShortUrl] = useState('');
  // Track mobile photo IDs already counted, so subscription doesn't double-count
  const trackedMobilePhotoUidsRef = useRef(new Set());
  // Cache mobile thumbnails/s3 data so they survive manager auto-removal
  const mobileVideoCacheRef = useRef({});

  // Upload section state
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [showUploadOptions, setShowUploadOptions] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showPlaceholderFolder, setShowPlaceholderFolder] = useState(false);
  const filesInputRef = useRef(null);
  const uploadsRef = useRef([]);
  // ── Recorded-clip registration ordering ──
  // Recorded clips must register (addVideo) in the order they were recorded,
  // but each clip registers in its own S3 onSuccess, which fires in
  // S3-completion (random) order. recordedSeqRef hands each recorded file a
  // monotonic index at capture time; recordedGatesRef holds a resolvable gate
  // per index so clip N's registration waits for clip N-1's to finish.
  const recordedSeqRef = useRef(0);
  const recordedGatesRef = useRef(new Map());
  // Highest recorded-clip index handed out so far. Non-recorded (picked/cloud)
  // uploads wait on this index's gate so they only register AFTER every
  // recorded clip — keeping recorded answers ahead of picked files in the
  // projector. -1 = no recorded clips yet (picked uploads register freely).
  const lastRecordedIndexRef = useRef(-1);
  const videoMetadataRef = useRef(new Map());
  const photoMetadataRef = useRef(new Map());
  const receiverNameRef = useRef(receiverName);

  // Content tab state
  const [contentTabType, setContentTabType] = useState('videos');
  const [contentLoading, setContentLoading] = useState(false);

  // Content data from API
  const [userVideos, setUserVideos] = useState([]);
  const [userAlbums, setUserAlbums] = useState([]);

  // Build the gift-popup entry per video-bearing folder. Every folder with
  // at least one video gets a pill — recorded prompts aren't required.
  //   • `clips` plays the WHOLE folder (recorded clips first in story
  //     order, then any remaining non-recorded videos) so opening the
  //     gift presents the full library, not just the prompt answers.
  //   • `recordedCount` is the count of clips matched to known prompts
  //     and is what the scratch-card "recorded N messages" copy uses;
  //     the pill itself shows the total video count via `clips.length`.
  //   • Non-recorded clips get a synthetic `subject` (their title) so
  //     the player's chapter labels and caption render uniformly.
  // Folders with only photos don't get an entry — the popup is a video
  // playback feature, so a 0-video pill would be meaningless.
  const giftClipsByFolder = useMemo(() => {
    const out = new Map();
    const groups = new Map();
    userVideos.forEach((v) => {
      const cat = v.category || 'All Videos';
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push(v);
    });
    groups.forEach((vids, cat) => {
      if (vids.length === 0) return;
      const result = findGiftMessageClips(vids);
      const recordedIds = new Set(result.clips.map((c) => c.id));
      const tail = vids
        .filter((v) => !recordedIds.has(v.id))
        .map((v) => ({ ...v, subject: v.title || 'Video' }));
      const clips = [...result.clips, ...tail];
      if (clips.length === 0) return;
      const senderLabel = cat.replace(/'s\s+\w+$/i, '').trim() || cat;
      out.set(cat, {
        clips,
        recordedCount: result.clips.length,
        fromName: senderLabel,
      });
    });
    return out;
  }, [userVideos]);

  // Soft-deleted items waiting for restore or permanent deletion
  const [deletedItems, setDeletedItems] = useState([]); // { type: 'video'|'album', ...itemData }
  const deletedItemsRef = useRef(deletedItems);

  // Raw pending actions from API (for executing on Next click)
  const [pendingActions, setPendingActions] = useState([]);
  const [executingActions, setExecutingActions] = useState(false);

  // Background 6-frame thumbnail generation for existing user (pre-generated for Next click)
  const bgThumbnailsRef = useRef({}); // { [actionId]: { promise, thumbnails, status } }
  const [bgThumbsReady, setBgThumbsReady] = useState(false);

  // Category folder state
  const [selectedCategory, setSelectedCategory] = useState(null);
  // Inner tab when inside a folder: 'all', 'videos', 'photos'
  const [folderTab, setFolderTab] = useState('all');

  // Per-folder gift popup — null when closed, otherwise the category name
  // whose clips are currently being shown.
  const [openGiftFolder, setOpenGiftFolder] = useState(null);
  const openGiftEntry = openGiftFolder ? giftClipsByFolder.get(openGiftFolder) : null;
  const handleOpenFolderGift = (categoryName) => {
    if (!giftClipsByFolder.has(categoryName)) return;
    setSelectedCategory(categoryName);
    setOpenGiftFolder(categoryName);
  };
  const handleCloseFolderGift = () => setOpenGiftFolder(null);

  // ── Receiver-side recorder: clicking Next opens the shared
  // PersonalVideoMessageFlow (tile-hub UX). Each section's recorded clips
  // are batched client-side until the user clicks Finish, at which point
  // the component fires onComplete(files, { fileSections }) and we run
  // them through the existing handleReceiverRecordingComplete → upload
  // pipeline so each clip lands in its section's category. ──
  const [showRecordPromptScreen, setShowRecordPromptScreen] = useState(false);

  const closeReceiverRecorder = () => {
    setShowRecordPromptScreen(false);
  };

  // Determine which category name uploads will land in and count active uploads
  const uploadingCategory = useMemo(() => (receiverName && receiverName !== 'Your' ? `${receiverName.trim()}'s videos` : 'All Videos'), [receiverName]);

  const activeUploads = useMemo(() => uploads.filter((u) => u.status === UPLOAD_STATUS.PENDING || u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PAUSED || u.status === UPLOAD_STATUS.NETWORK_ERROR || u.status === UPLOAD_STATUS.S3_COMPLETE), [uploads]);

  const activeVideoUploads = useMemo(() => activeUploads.filter((u) => u.type === 'video'), [activeUploads]);
  const activeAlbumUploads = useMemo(() => activeUploads.filter((u) => u.type === 'album'), [activeUploads]);

  // Derive unique categories from video data, with video and photo counts + grid thumbnails
  const categories = useMemo(() => {
    const catMap = {};
    userVideos.forEach((video) => {
      const cat = video.category || 'All Videos';
      if (!catMap[cat]) {
        catMap[cat] = { name: cat, videoCount: 0, photoCount: 0, thumbnail: video.thumbnail, gridThumbnails: [] };
      }
      catMap[cat].videoCount += 1;
      if (isExistingUser && video.isPending && video.thumbFailed && video.videoUrl) {
        catMap[cat].gridThumbnails.push({ type: 'video', src: video.videoUrl });
      } else if (video.thumbnail && video.thumbnail !== image04) {
        catMap[cat].gridThumbnails.push({ type: 'image', src: video.thumbnail });
      }
    });
    // Count photos per category by matching album contributor name to video folder name
    // If no matching video folder exists, create a folder from the album's contributor name
    userAlbums.forEach((album) => {
      // Extract contributor name: "Shyam's albums" → "shyam", "Shyam's photos" → "shyam"
      const albumContributor = (album.title || '').replace(/'s\s+\w+$/i, '').trim().toLowerCase();
      let matched = false;
      // Try to match into existing video folder by contributor name (e.g. "Shyam's videos" contains "shyam")
      Object.keys(catMap).forEach((key) => {
        const catContributor = catMap[key].name.replace(/'s\s+\w+$/i, '').trim().toLowerCase();
        if (!matched && albumContributor && catContributor && albumContributor === catContributor) {
          catMap[key].photoCount += album.photoCount || 0;
          (album.photos || []).forEach((p) => {
            if (p.url) catMap[key].gridThumbnails.push({ type: 'image', src: p.url });
          });
          matched = true;
        }
      });
      // No matching video folder — create folder using contributor name (e.g. "Shyam's albums" → "Shyam's videos")
      if (!matched && albumContributor) {
        // Capitalize first letter for display
        const displayName = albumContributor.charAt(0).toUpperCase() + albumContributor.slice(1);
        const folderName = `${displayName}'s videos`;
        if (!catMap[folderName]) {
          const firstPhoto = (album.photos || []).find((p) => p.url);
          catMap[folderName] = { name: folderName, videoCount: 0, photoCount: 0, thumbnail: firstPhoto?.url || album.thumbnail || image01, gridThumbnails: [] };
        }
        catMap[folderName].photoCount += album.photoCount || 0;
        (album.photos || []).forEach((p) => {
          if (p.url) catMap[folderName].gridThumbnails.push({ type: 'image', src: p.url });
        });
      }
    });
    // Inject a placeholder folder if receiver doesn't already have a folder (stays until fetchContent returns data)
    if (activeUploads.length > 0 || showPlaceholderFolder) {
      const receiverFirstName = (receiverName || '').trim().toLowerCase();
      const hasExistingFolder = receiverFirstName && receiverFirstName !== 'your'
        && Object.keys(catMap).some((key) => key.toLowerCase().startsWith(receiverFirstName));
      if (!hasExistingFolder) {
        catMap[uploadingCategory] = { name: uploadingCategory, videoCount: 0, photoCount: 0, thumbnail: image04, gridThumbnails: [], isUploading: true };
      }
    }
    return Object.values(catMap);
  }, [userVideos, userAlbums, activeUploads, uploadingCategory, receiverName, showPlaceholderFolder]);

  // Filtered videos for selected category
  const filteredVideos = useMemo(() => {
    if (!selectedCategory) return userVideos;
    return userVideos.filter((v) => (v.category || 'All Videos') === selectedCategory);
  }, [userVideos, selectedCategory]);

  // Filtered albums for selected category — match by contributor name from title or category
  const filteredAlbums = useMemo(() => {
    if (!selectedCategory) return userAlbums;
    const name = selectedCategory.replace(/'s\s+\w+$/i, '').trim().toLowerCase();
    return userAlbums.filter((a) => {
      const fromTitle = (a.title || '').replace(/'s\s+\w+$/i, '').trim().toLowerCase();
      const fromCategory = (a.category || '').replace(/'s\s+\w+$/i, '').trim().toLowerCase();
      return fromTitle === name || fromCategory === name;
    });
  }, [userAlbums, selectedCategory]);

  // Video preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);

  // Album photos modal
  const [albumOpen, setAlbumOpen] = useState(false);
  const [albumLoading, setAlbumLoading] = useState(false);
  const [albumDetail, setAlbumDetail] = useState(null);

  // Fetch all content (all pages) using same API as YourVideos
  const fetchContent = useCallback(async () => {
    if (!getToken()) return;
    try {
      let allVideos = [];
      let allAlbums = [];
      let currentPage = 1;
      let lastPage = 1;

      // Loop through all pages to fetch everything
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await apiAxios.post(`${baseUrl}/V1/getMyContentsBasedOnUploadDate`, {
          page: currentPage,
        });

        if (response.data?.success && response.data?.data) {
          const { data, paginate } = response.data;
          allVideos = [...allVideos, ...(data.video || [])];
          allAlbums = [...allAlbums, ...(data.album || [])];

          if (paginate?.last_page) {
            lastPage = paginate.last_page;
          } else if (paginate?.total_pages) {
            lastPage = paginate.total_pages;
          }
        } else {
          break;
        }
        currentPage += 1;
      } while (currentPage <= lastPage);

      // Map videos
      const videos = allVideos.map((video) => {
        const videoThumbnail = Array.isArray(video.thumbnail)
          ? video.thumbnail[0] || image04
          : video.thumbnail || video.thumbnails || image04;
        return {
          id: video.content_id || video.video_id || video.id,
          title: video.title || 'Untitled',
          thumbnail: videoThumbnail,
          duration: video.duration || video.video_duration || '',
          date: video.publish_date || video.created || '',
          category: video.category_name || video.category || 'All Videos',
        };
      });
      setUserVideos(videos);

      // Map albums first without photo counts
      const albums = allAlbums.map((album) => {
        const albumThumbnail = Array.isArray(album.thumbnail)
          ? album.thumbnail[0] || image01
          : album.thumbnail || album.thumbnails || image01;
        console.log('[GIFT_CONTENT] Album mapping:', {
          id: album.content_id || album.id || album.album_id,
          title: album.title,
          rawThumbnail: album.thumbnail,
          rawThumbnails: album.thumbnails,
          resolvedThumbnail: albumThumbnail,
        });
        return {
          id: album.content_id || album.id || album.album_id,
          title: album.title || album.album_title || 'Untitled Album',
          thumbnail: albumThumbnail,
          photoCount: 0,
          date: album.created_at || album.upload_date || album.created || '',
          category: album.category_name || album.category || 'All Videos',
        };
      });
      setUserAlbums(albums);

      // Fetch photo counts and first 9 photos in batches of 5
      const batchSize = 5;
      for (let i = 0; i < albums.length; i += batchSize) {
        const batch = albums.slice(i, i + batchSize);
        // eslint-disable-next-line no-await-in-loop
        const results = await Promise.all(batch.map(async (album) => {
          try {
            const res = await apiAxios.post(`${baseUrl}/getMyAlbumDetail`, {
              album_id: album.id,
              token: getToken(),
            });
            const photos = res?.data?.data?.[0]?.photos || res?.data?.data?.photos || [];
            console.log('[GIFT_CONTENT] Album detail for', album.id, ':', {
              photoCount: res?.data?.data?.[0]?.photo_count || photos.length,
              photosReturned: photos.length,
              firstPhotoUrl: photos[0] ? (photos[0].imagekit_url || photos[0].photo_file || 'none') : 'no photos',
              rawFirstPhoto: photos[0],
            });
            return {
              id: album.id,
              photoCount: res?.data?.data?.[0]?.photo_count || photos.length || 0,
              photos: photos.slice(0, 9).map((p) => ({
                id: p.id,
                url: p.imagekit_url || p.photo_file || null,
                name: p.title || p.name || '',
              })),
            };
          } catch (e) {
            return { id: album.id, photoCount: 0, photos: [] };
          }
        }));
        // Update albums with photo counts and photos as each batch completes
        setUserAlbums((prev) => prev.map((a) => {
          const match = results.find((r) => r.id === a.id);
          return match ? { ...a, photoCount: match.photoCount, photos: match.photos } : a;
        }));
      }
      return { videoCount: videos.length, albumCount: albums.length };
    } catch (err) {
      console.error('[GIFT_CONTENT] Error fetching content:', err);
      setSnackbar({ isVisible: true, message: 'Failed to load content', type: 'error' });
      return { videoCount: 0, albumCount: 0 };
    }
  }, []);

  // Open video preview modal - fetch video details to get video_raw_file
  const handleVideoClick = async (video) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewVideo({ title: video.title, thumbnail: video.thumbnail });

    // Existing user: video not registered yet, construct S3 URL from file_key
    if (isExistingUser && video.isPending && video.fileKey) {
      const s3Url = generateS3Url();
      const videoUrl = `${s3Url}${video.fileKey}`;
      setPreviewVideo({
        title: video.title,
        thumbnail: video.thumbnail,
        videoUrl,
      });
      setPreviewLoading(false);
      return;
    }

    try {
      const response = await getVideosDetail({ id: video.id });
      if (response?.data?.success && response?.data?.data) {
        const details = response.data.data;
        const videoUrl = details.video_raw_file || '';
        setPreviewVideo({
          title: details.title || video.title,
          thumbnail: video.thumbnail,
          videoUrl,
        });
      }
    } catch (err) {
      console.error('[GIFT_CONTENT] Error fetching video details:', err);
      setSnackbar({ isVisible: true, message: 'Failed to load video preview', type: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewVideo(null);
  };

  // Open album photos modal - fetch album details using same API as main app
  const handleAlbumClick = async (album) => {
    setAlbumOpen(true);

    // Existing user: photos already available from pending actions, no API call needed
    if (album.isPending && album.photos) {
      setAlbumDetail({
        title: album.title,
        thumbnail: album.thumbnail,
        photos: album.photos,
        albumId: album.id,
      });
      setAlbumLoading(false);
      return;
    }

    setAlbumLoading(true);
    setAlbumDetail({ title: album.title, thumbnail: album.thumbnail, photos: [], albumId: album.id });
    try {
      const response = await apiAxios.post(`${baseUrl}/getMyAlbumDetail`, {
        album_id: album.id,
      });
      if (response?.data?.success && response?.data?.data) {
        const { data } = response.data;
        const photos = data[0]?.photos || data?.photos || [];
        const deletedPhotoIds = new Set(deletedItems.filter((d) => d.type === 'photo').map((d) => String(d.id)));
        setAlbumDetail({
          title: data[0]?.title || album.title,
          thumbnail: album.thumbnail,
          photos: photos.filter((p) => !deletedPhotoIds.has(String(p.id))),
          albumId: album.id,
        });
      }
    } catch (err) {
      console.error('[GIFT_CONTENT] Error fetching album details:', err);
      setSnackbar({ isVisible: true, message: 'Failed to load album photos', type: 'error' });
    } finally {
      setAlbumLoading(false);
    }
  };

  const closeAlbum = () => {
    setAlbumOpen(false);
    setAlbumDetail(null);
  };

  // Soft delete — move item to Restore tab instead of calling API
  const handleSoftDelete = (type, item, e) => {
    e.stopPropagation();
    console.log('[GIFT_CONTENT] Soft delete:', type, item.id, item.title);
    if (type === 'video') {
      setUserVideos((prev) => prev.filter((v) => v.id !== item.id));
    } else {
      setUserAlbums((prev) => prev.filter((a) => a.id !== item.id));
    }
    setDeletedItems((prev) => {
      const updated = [...prev, { ...item, type }];
      console.log('[GIFT_CONTENT] deletedItems after add:', updated.length);
      return updated;
    });
    setSnackbar({ isVisible: true, message: 'Moved to Restore tab', type: 'success' });
  };

  // Restore — move item back from Restore tab to its original list
  const handleRestore = (item) => {
    setDeletedItems((prev) => prev.filter((d) => !(d.id === item.id && d.type === item.type)));
    if (item.type === 'video') {
      setUserVideos((prev) => [...prev, item]);
      setSnackbar({ isVisible: true, message: 'Video restored', type: 'success' });
    } else if (item.type === 'photo') {
      // Restore photo back into its album
      setUserAlbums((prev) => prev.map((a) => {
        if (String(a.id) === String(item.albumId)) {
          const restoredPhoto = item.photoData || { id: item.id, url: item.thumbnail, name: item.title };
          return { ...a, photoCount: (a.photoCount || 0) + 1, photos: [...(a.photos || []), restoredPhoto] };
        }
        return a;
      }));
      // Also restore into album detail modal if it's currently open
      if (albumDetail && String(albumDetail.albumId) === String(item.albumId)) {
        setAlbumDetail((prev) => ({
          ...prev,
          photos: [...(prev.photos || []), item.photoData],
        }));
      }
      setSnackbar({ isVisible: true, message: 'Photo restored', type: 'success' });
    } else {
      setUserAlbums((prev) => [...prev, item]);
      setSnackbar({ isVisible: true, message: 'Album restored', type: 'success' });
    }
  };

  // The actual original Next handler (now invoked after the record-prompt
  // is answered). The wrapper below intercepts to show the Yes/No screen.
  const handleNextClickOriginal = async () => {
    // Existing user flow: execute non-deleted pending actions, then clear
    if (isExistingUser && giftUid && pendingActions.length > 0) {
      setExecutingActions(true);

      // Wait for background thumbnail generation if not done yet
      if (!bgThumbsReady) {
        console.log('[GIFT_CONTENT] Waiting for background thumbnail generation to complete...');
        await new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            const allDone = Object.values(bgThumbnailsRef.current).every((t) => t.status === 'done');
            if (allDone) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 500);
        });
        console.log('[GIFT_CONTENT] Background thumbnails ready, proceeding');
      }

      // Get IDs of deleted items to skip
      const deletedIds = new Set(deletedItems.map((d) => d.id));

      // Filter actions that are NOT deleted
      const actionsToExecute = pendingActions.filter((a) => !deletedIds.has(a.action_id));
      console.log('[GIFT_CONTENT] Executing', actionsToExecute.length, 'of', pendingActions.length, 'pending actions');

      // Group createAndAttachAlbum actions by title so each contributor gets their own album.
      // Same-title actions are merged (multi-drop by one contributor → one album).
      const albumActions = actionsToExecute.filter((a) => a.action_type === 'createAndAttachAlbum');
      const nonAlbumActions = actionsToExecute.filter((a) => a.action_type !== 'createAndAttachAlbum');

      const mergedAlbumActions = [];
      if (albumActions.length > 0) {
        const groupsByTitle = new Map();
        albumActions.forEach((a) => {
          const title = a.payload?.title || 'Untitled Album';
          if (!groupsByTitle.has(title)) groupsByTitle.set(title, []);
          groupsByTitle.get(title).push(a);
        });
        groupsByTitle.forEach((groupActions) => {
          const allPhotos = groupActions.flatMap((a) => a.payload?.photos || []);
          mergedAlbumActions.push({
            ...groupActions[0],
            payload: {
              ...groupActions[0].payload,
              photos: allPhotos,
              icon: allPhotos[0]?.path || groupActions[0].payload?.icon || '',
            },
          });
        });
        console.log('[GIFT_CONTENT] Grouped', albumActions.length, 'album actions into', mergedAlbumActions.length, 'album(s) by title');
      }

      const finalActions = [...nonAlbumActions, ...mergedAlbumActions];

      // Execute actions. addVideo actions must run one-after-another in their
      // stored order (== the order the sender recorded them, since they were
      // stored sequentially) so recorded clips register in question order.
      // Album actions carry no order and run in parallel alongside.
      const runAction = async (action) => {
        if (action.action_type === 'addVideo') {
          const formData = new FormData();
          Object.entries(action.payload).forEach(([key, value]) => {
            formData.append(key, value);
          });

          // Use pre-generated background thumbnails
          const bgThumb = bgThumbnailsRef.current[action.action_id];
          const thumbs = bgThumb?.thumbnails || [];

          if (thumbs.length > 0) {
            console.log('[THUMB_DEBUG] Registration — source:', bgThumb.source, '| frames:', thumbs.length, '| action:', action.action_id);
            formData.append('thumbnail', base64ToBinary(thumbs[0]));
            thumbs.forEach((t) => {
              if (t) {
                try {
                  const binaryThumb = typeof t === 'string' ? base64ToBinary(t) : t;
                  formData.append('thumbnails[]', binaryThumb);
                } catch (thumbErr) {
                  console.error('[GIFT_CONTENT] Thumbnail conversion failed:', thumbErr);
                }
              }
            });
          } else {
            // No 6-frame thumbnails — check if single-frame display thumbnail exists
            const matchingVideo = userVideos.find((v) => v.id === action.action_id);
            const singleThumb = matchingVideo?.thumbnail;
            if (singleThumb && singleThumb !== image04 && singleThumb.startsWith('data:')) {
              console.log('[THUMB_DEBUG] Registration — source: single-frame-display | action:', action.action_id);
              formData.append('thumbnail', base64ToBinary(singleThumb));
              formData.append('thumbnails[]', base64ToBinary(singleThumb));
            } else {
            // No thumbnails at all — send projector logo fallback
            console.log('[THUMB_DEBUG] Registration — source: projector-logo-fallback | action:', action.action_id);
            try {
              const fallbackResp = await fetch(image04);
              const fallbackBlob = await fallbackResp.blob();
              const fallbackBase64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(fallbackBlob);
              });
              formData.append('thumbnail', base64ToBinary(fallbackBase64));
              formData.append('thumbnails[]', base64ToBinary(fallbackBase64));
            } catch (fallbackErr) {
              console.warn('[GIFT_CONTENT] Fallback thumbnail failed:', fallbackErr);
            }
            }
          }

          formData.append('token', getToken());
          return apiAxios.post(`${baseUrl}${action.endpoint}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          }).then(() => {
            console.log('[GIFT_CONTENT] addVideo completed:', action.action_id);
          }).catch((err) => {
            console.error('[GIFT_CONTENT] Failed to execute addVideo:', action.action_id, err);
          });
        }
        if (action.action_type === 'createAndAttachAlbum') {
          // Filter out photos that user moved to restore tab
          const deletedPhotoNames = new Set(
            deletedItems.filter((d) => d.type === 'photo').map((d) => d.id)
          );
          const remainingPhotos = (action.payload.photos || []).filter(
            (p) => !deletedPhotoNames.has(p.name)
          );

          // Skip album entirely if all photos were deleted
          if (remainingPhotos.length === 0) {
            console.log('[GIFT_CONTENT] All photos deleted from album, skipping:', action.action_id);
            return Promise.resolve();
          }

          return apiAxios.post(`${baseUrl}${action.endpoint}`, {
            ...action.payload,
            photos: remainingPhotos,
            icon: remainingPhotos[0]?.path || action.payload.icon || '',
            token: getToken(),
          }).then(() => {
            console.log('[GIFT_CONTENT] createAndAttachAlbum completed:', action.action_id, 'photos:', remainingPhotos.length);
          }).catch((err) => {
            console.error('[GIFT_CONTENT] Failed to execute createAndAttachAlbum:', action.action_id, err);
          });
        }
        return Promise.resolve();
      };

      const videoActionsInOrder = finalActions.filter((a) => a.action_type === 'addVideo');
      const otherActions = finalActions.filter((a) => a.action_type !== 'addVideo');
      // Albums (and anything else) concurrently…
      const otherActionsPromise = Promise.all(otherActions.map((a) => runAction(a)));
      // …addVideo strictly in order.
      for (let i = 0; i < videoActionsInOrder.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await runAction(videoActionsInOrder[i]);
      }
      await otherActionsPromise;

      // Clear all pending actions from backend
      try {
        await clearGiftActions({ gift_uuid: giftUid, mark_completed: true });
        console.log('[GIFT_CONTENT] Gift actions marked as completed');
      } catch (err) {
        console.error('[GIFT_CONTENT] Failed to clear gift actions:', err);
      }

      setExecutingActions(false);
      navigate(`${Pathname.profile.basePath}?fromGift=true`);
      return;
    }

    // Normal flow: fire delete API calls for soft-deleted items
    if (deletedItems.length > 0) {
      const deletePromises = deletedItems.map((item) => {
        if (item.type === 'video') {
          return apiAxios.post(`${baseUrl}/deleteVideo`, { video_id: item.id }).catch((err) => {
            console.error('[GIFT_CONTENT] Failed to delete video:', item.id, err);
          });
        }
        if (item.type === 'photo') {
          return apiAxios.post(`${baseUrl}/deleteMyPhoto`, { photo_id: item.id }).catch((err) => {
            console.error('[GIFT_CONTENT] Failed to delete photo:', item.id, err);
          });
        }
        return apiAxios.post(`${baseUrl}/deleteMyAlbum`, { album_id: item.id }).catch((err) => {
          console.error('[GIFT_CONTENT] Failed to delete album:', item.id, err);
        });
      });
      Promise.all(deletePromises);
    }
    navigate(`${Pathname.authentication.ecosystem}?fromGift=true`, { state: { isGiftFlow: true } });
  };

  // ── Wrapper around the original Next handler. First click intercepts and
  // shows the "Add a personal video message" info screen. Once the user has
  // proceeded through it, subsequent Next clicks go straight through. ──
  const handleNextClick = async () => {
    // Feature flag off → never show the recorder intercept. Goes straight to
    // the original Next behaviour (existing flow before the recorder was
    // introduced).
    if (!VIDEO_RECORDER_ENABLED) {
      handleNextClickOriginal();
      return;
    }
    // Gate the recorder intercept on a one-time localStorage "asked" flag.
    // The first Next click shows the "Add a personal video message" prompt
    // and sets the flag; subsequent Next clicks read the flag and go straight
    // through to the original Next behaviour (ecosystem/device screen).
    let asked = false;
    try { asked = localStorage.getItem('giftReceiverRecordPromptAsked') === 'true'; } catch (e) { asked = false; }
    if (asked) {
      handleNextClickOriginal();
      return;
    }
    // The "asked" flag is only written when the user explicitly Skips the
    // flow (see onSkip). Until then, every Next re-opens this page — and Back
    // leaves the flag unset so it keeps coming back.
    setShowRecordPromptScreen(true);
  };

  // (Proceed handler no longer needed — PersonalVideoMessageFlow owns the
  // info → tile hub → recorder transitions internally. The localStorage
  // "asked once" flag is set inside handleNextClick when the prompt first
  // opens, so subsequent Next clicks bypass the prompt.)

  // Fetch pending gift actions for existing users and map to videos/albums
  const fetchPendingActions = useCallback(async (cancelled) => {
    console.log('[GIFT_CONTENT] fetchPendingActions called — giftUid:', giftUid);
    if (!giftUid) {
      console.log('[GIFT_CONTENT] No giftUid, skipping');
      return { videoCount: 0, albumCount: 0 };
    }
    try {
      console.log('[GIFT_CONTENT] Calling getGiftPendingActions API...');
      const response = await getGiftPendingActions(giftUid);
      console.log('[GIFT_CONTENT] getGiftPendingActions response:', response?.data);
      if (response?.data?.success && response?.data?.data?.pending_actions) {
        const actions = response.data.data.pending_actions;
        const s3Base = 'https://projector-optimized.s3.us-west-2.amazonaws.com/';

        const pendingVideos = actions
          .filter((a) => a.action_type === 'addVideo')
          .map((a, idx) => ({
            id: a.action_id || `pending_vid_${idx}`,
            title: a.payload?.title || a.payload?.file_name || 'Untitled',
            thumbnail: image04,
            duration: '',
            date: '',
            category: a.payload?.category_name || 'All Videos',
            isPending: true,
            fileKey: a.payload?.file_key || '',
            videoUrl: a.payload?.file_key ? `${s3Base}${a.payload.file_key}` : '',
          }));

        // Group createAndAttachAlbum actions by title so each contributor's album shows
        // as its own folder. Same-title actions still merge (multi-drop by one contributor),
        // but different titles (sender vs receiver, etc.) stay separate.
        const albumActionsList = actions.filter((a) => a.action_type === 'createAndAttachAlbum');
        const pendingAlbums = [];
        if (albumActionsList.length > 0) {
          const groupsByTitle = new Map();
          albumActionsList.forEach((a) => {
            const title = a.payload?.title || 'Untitled Album';
            if (!groupsByTitle.has(title)) groupsByTitle.set(title, []);
            groupsByTitle.get(title).push(a);
          });
          groupsByTitle.forEach((groupActions, title) => {
            const allPhotos = groupActions.flatMap((a) => a.payload?.photos || []);
            const firstPhotoPath = allPhotos[0]?.path || groupActions[0].payload?.icon || '';
            pendingAlbums.push({
              id: groupActions[0].action_id || `pending_album_${pendingAlbums.length}`,
              title,
              thumbnail: firstPhotoPath ? `${s3Base}${firstPhotoPath}` : image01,
              photoCount: allPhotos.length,
              date: '',
              category: '',
              isPending: true,
              photos: allPhotos.slice(0, 9).map((p) => ({
                id: p.name,
                url: p.path ? `${s3Base}${p.path}` : null,
                name: p.name || '',
              })),
            });
          });
        }

        if (cancelled) return;
        setPendingActions(actions);

        // Merge new videos: keep existing thumbnails & data, only add truly new ones
        // Compute new videos before state update so we can generate thumbnails after
        const deletedIds = new Set(deletedItemsRef.current.map((d) => d.id));
        setUserVideos((prev) => {
          const existingIds = new Set(prev.map((v) => v.id));
          // Keep existing videos that are still in pending actions (preserve thumbnails)
          const kept = prev.filter((v) => actions.some((a) => a.action_id === v.id));
          // Find new videos not already displayed and not in restore tab
          const newVideos = pendingVideos.filter((v) => !existingIds.has(v.id) && !deletedIds.has(v.id));
          return [...kept, ...newVideos];
        });

        // Generate single-frame thumbnails for display; if client fails, try backend API fallback
        pendingVideos.filter((v) => !deletedIds.has(v.id)).forEach((video) => {
          if (!video.videoUrl) return;
          generateSingleThumbnail(video.videoUrl).then(async (thumb) => {
            if (cancelled) return;
            if (thumb && thumb !== image04) {
              console.log('[GIFT_CONTENT] Thumbnail source: client-generated for:', video.id);
              setUserVideos((p) => p.map((v) => (
                v.id === video.id ? { ...v, thumbnail: thumb, thumbFailed: false, thumbSource: 'client' } : v
              )));
            } else {
              // Client failed — try backend API fallback
              console.log('[GIFT_CONTENT] Client thumbnail failed for:', video.id, '— trying backend API');
              const backendThumb = await callBackendThumbnailFallback(video.videoUrl);
              if (cancelled) return;
              if (backendThumb) {
                console.log('[GIFT_CONTENT] Thumbnail source: backend-api for:', video.id);
                setUserVideos((p) => p.map((v) => (
                  v.id === video.id ? { ...v, thumbnail: backendThumb, thumbFailed: false, thumbSource: 'backend' } : v
                )));
              } else {
                console.warn('[GIFT_CONTENT] Both client & backend failed for:', video.id, '— using <video> fallback');
                setUserVideos((p) => p.map((v) => (
                  v.id === video.id ? { ...v, thumbFailed: true, thumbSource: 'none' } : v
                )));
              }
            }
          }).catch(async () => {
            if (cancelled) return;
            // Client threw — try backend API fallback
            console.log('[GIFT_CONTENT] Client thumbnail threw for:', video.id, '— trying backend API');
            const backendThumb = await callBackendThumbnailFallback(video.videoUrl);
            if (cancelled) return;
            if (backendThumb) {
              console.log('[GIFT_CONTENT] Thumbnail source: backend-api for:', video.id);
              setUserVideos((p) => p.map((v) => (
                v.id === video.id ? { ...v, thumbnail: backendThumb, thumbFailed: false, thumbSource: 'backend' } : v
              )));
            } else {
              console.warn('[GIFT_CONTENT] Both client & backend failed for:', video.id, '— using <video> fallback');
              setUserVideos((p) => p.map((v) => (
                v.id === video.id ? { ...v, thumbFailed: true, thumbSource: 'none' } : v
              )));
            }
          });
        });

        // Replace albums with freshly built pendingAlbums so newly added photos in the
        // same contributor group show up immediately. Albums have no client-side state
        // worth preserving across refetches (no client-generated thumbnails).
        setUserAlbums(() => {
          const deletedIds = new Set(deletedItemsRef.current.map((d) => d.id));
          return pendingAlbums.filter((a) => !deletedIds.has(a.id));
        });

        // Start background 6-frame thumbnail generation for all pending videos
        if (isExistingUser) {
          setBgThumbsReady(false);
          const videoActions = actions.filter((a) => a.action_type === 'addVideo' && !deletedIds.has(a.action_id));
          const s3Base = generateS3Url();
          const promises = videoActions.map((action) => {
            const videoUrl = action.payload?.file_key ? `${s3Base}${action.payload.file_key}` : '';
            if (!videoUrl || bgThumbnailsRef.current[action.action_id]?.status === 'done') {
              return Promise.resolve();
            }
            bgThumbnailsRef.current[action.action_id] = { status: 'generating', thumbnails: [] };
            console.log('[BG_THUMB] Starting 6-frame generation for:', action.action_id);
            return generateThumbnailsFromUrl(videoUrl).then(async (result) => {
              if (cancelled) return;
              if (result.thumbnails && result.thumbnails.length > 0 && result.thumbnails[0] !== image04) {
                bgThumbnailsRef.current[action.action_id] = { status: 'done', thumbnails: result.thumbnails, source: 'client' };
                console.log('[BG_THUMB] Done (client):', action.action_id, '— frames:', result.thumbnails.length);
              } else {
                // Client failed, try backend fallback
                console.log('[BG_THUMB] Client failed for:', action.action_id, '— trying backend');
                const backendThumb = await callBackendThumbnailFallback(videoUrl);
                if (backendThumb) {
                  bgThumbnailsRef.current[action.action_id] = { status: 'done', thumbnails: [backendThumb], source: 'backend' };
                  console.log('[BG_THUMB] Done (backend):', action.action_id);
                } else {
                  bgThumbnailsRef.current[action.action_id] = { status: 'done', thumbnails: [], source: 'none' };
                  console.warn('[BG_THUMB] All methods failed for:', action.action_id);
                }
              }
            }).catch(async () => {
              if (cancelled) return;
              const backendThumb = await callBackendThumbnailFallback(videoUrl);
              if (backendThumb) {
                bgThumbnailsRef.current[action.action_id] = { status: 'done', thumbnails: [backendThumb], source: 'backend' };
              } else {
                bgThumbnailsRef.current[action.action_id] = { status: 'done', thumbnails: [], source: 'none' };
              }
            });
          });

          Promise.all(promises).then(() => {
            if (!cancelled) {
              console.log('[BG_THUMB] All background thumbnail generations complete');
              setBgThumbsReady(true);
            }
          });
        }
        return { videoCount: pendingVideos.length, albumCount: pendingAlbums.length };
      }
      return { videoCount: 0, albumCount: 0 };
    } catch (err) {
      console.error('[GIFT_CONTENT] Error fetching pending actions:', err);
      setSnackbar({ isVisible: true, message: 'Failed to load pending content', type: 'error' });
      return { videoCount: 0, albumCount: 0 };
    }
  }, [giftUid]);

  // Fetch all content on mount
  useEffect(() => {
    let cancelled = false;
    console.log('[GIFT_CONTENT] Mount — isExistingUser:', isExistingUser, 'giftUid:', giftUid, 'token:', !!getToken());
    setContentLoading(true);
    if (isExistingUser && giftUid) {
      console.log('[GIFT_CONTENT] Existing user flow — fetching pending actions for uid:', giftUid);
      fetchPendingActions(cancelled).finally(() => { if (!cancelled) setContentLoading(false); });
    } else if (getToken()) {
      console.log('[GIFT_CONTENT] Normal flow — fetching registered content');
      fetchContent().finally(() => { if (!cancelled) setContentLoading(false); });
    } else {
      console.log('[GIFT_CONTENT] No token and not existing user — skipping fetch');
      setContentLoading(false);
    }
    return () => { cancelled = true; };
  }, [fetchContent, fetchPendingActions, isExistingUser, giftUid]);

  // If restore tab becomes empty, switch back to videos
  useEffect(() => {
    if (contentTabType === 'restore' && deletedItems.length === 0) {
      setContentTabType('videos');
    }
  }, [deletedItems.length, contentTabType]);

  // Keep refs in sync
  useEffect(() => { deletedItemsRef.current = deletedItems; }, [deletedItems]);
  useEffect(() => { uploadsRef.current = uploads; }, [uploads]);
  useEffect(() => { receiverNameRef.current = receiverName; }, [receiverName]);

  // Auto-refresh content when all uploads finish (COMPLETED/FAILED/CANCELLED — after onSuccess runs)
  useEffect(() => {
    if (uploads.length === 0) return;
    const allFinished = uploads.every(
      (u) => u.status === UPLOAD_STATUS.COMPLETED || u.status === UPLOAD_STATUS.FAILED || u.status === UPLOAD_STATUS.CANCELLED,
    );
    if (allFinished) {
      let cancelled = false;
      const hasCompletedUpload = uploads.some((u) => u.status === UPLOAD_STATUS.COMPLETED);
      // Give backend a moment to index, then refresh content (keep uploads in history)
      // Retry if backend hasn't indexed the new video yet (first-video scenario)
      const fetchAndCheck = (retriesLeft) => {
        if (cancelled) return;
        const delay = retriesLeft === 3 ? 2000 : 3000;
        setTimeout(async () => {
          if (cancelled) return;
          if (isExistingUser && giftUid) {
            const result = await fetchPendingActions();
            if (cancelled) return;
            const hasContent = result && (result.videoCount > 0 || result.albumCount > 0);
            if (hasContent || !hasCompletedUpload || retriesLeft <= 0) {
              setShowPlaceholderFolder(false);
            } else {
              // Backend hasn't recorded the pending action yet — retry
              fetchAndCheck(retriesLeft - 1);
            }
          } else if (getToken()) {
            const result = await fetchContent();
            if (cancelled) return;
            const hasContent = result && (result.videoCount > 0 || result.albumCount > 0);
            if (hasContent || !hasCompletedUpload || retriesLeft <= 0) {
              setShowPlaceholderFolder(false);
            } else {
              // Backend hasn't indexed yet — retry
              fetchAndCheck(retriesLeft - 1);
            }
          }
        }, delay);
      };
      fetchAndCheck(3);
      return () => { cancelled = true; };
    }
    return undefined;
  }, [uploads, isExistingUser, giftUid, fetchPendingActions, fetchContent]);

  // Subscribe to uploadManagerSecure for progress updates
  useEffect(() => {
    const unsubscribe = uploadManagerSecure.subscribe(({ uploads: managerUploads }) => {
      if (managerUploads.length === 0) return;

      setUploads((prev) => {
        let changed = false;
        let updated = prev.map((upload) => {
          // Local file upload — match by secureUploadId; mobile remote upload — match by mobileUid
          const managerUpload = upload.secureUploadId
            ? managerUploads.find((u) => u.draftId === upload.secureUploadId)
            : upload.isMobileUpload && !upload.isPhotoAlbum
              ? managerUploads.find((u) => u.id === upload.mobileUid)
              : null;
          if (!managerUpload) return upload;
          if (upload.status === UPLOAD_STATUS.COMPLETED) return upload;

          let mappedStatus = upload.status;
          if (managerUpload.status === 'pending') mappedStatus = UPLOAD_STATUS.PENDING;
          else if (managerUpload.status === 'initializing' || managerUpload.status === 'uploading' || managerUpload.status === 'finishing') mappedStatus = UPLOAD_STATUS.UPLOADING;
          else if (managerUpload.status === 'paused') mappedStatus = UPLOAD_STATUS.PAUSED;
          else if (managerUpload.status === 'network_error') mappedStatus = UPLOAD_STATUS.NETWORK_ERROR;
          else if (managerUpload.status === 'failed') mappedStatus = UPLOAD_STATUS.FAILED;
          else if (managerUpload.status === 'cancelled') mappedStatus = UPLOAD_STATUS.CANCELLED;
          else if (managerUpload.status === 's3_complete' || managerUpload.status === 'completed') mappedStatus = UPLOAD_STATUS.COMPLETED;

          if (
            mappedStatus === upload.status
            && managerUpload.progress === upload.progress
            && managerUpload.speed === upload.speed
          ) {
            return upload;
          }

          changed = true;
          // For local uploads keep the existing 0.9x scaling; mobile uploads come in as
          // already-finalized progress (the SSE stream marks them complete directly).
          const managerProgress = managerUpload.status === 's3_complete' || mappedStatus === UPLOAD_STATUS.COMPLETED
            ? 100
            : (upload.isMobileUpload
              ? Math.round(managerUpload.progress || 0)
              : Math.round(managerUpload.progress * 0.9));

          // For mobile videos, also pick up s3Url/thumbnail as soon as they appear
          const s3Base = generateS3Url();
          const newS3Url = upload.isMobileUpload && !upload.s3Url && managerUpload.s3Key
            ? `${s3Base}${managerUpload.s3Key}`
            : upload.s3Url;
          const newThumbnail = upload.isMobileUpload
            ? (managerUpload.thumbnail || upload.thumbnail || null)
            : upload.thumbnail;

          return {
            ...upload,
            status: mappedStatus,
            progress: Math.max(managerProgress, upload.progress || 0),
            bytesUploaded: Math.max(managerUpload.bytesUploaded || 0, upload.bytesUploaded || 0),
            speed: managerUpload.speed || upload.speed,
            timeRemaining: managerUpload.timeRemaining || upload.timeRemaining,
            isPaused: managerUpload.isPaused || false,
            s3Url: newS3Url,
            s3Key: upload.isMobileUpload ? (managerUpload.s3Key || upload.s3Key) : upload.s3Key,
            thumbnail: newThumbnail,
          };
        });

        // Pick up new remote (mobile) uploads not yet in state.
        // SSE handler in app.jsx populates these via uploadManagerSecure.createRemoteUpload.
        const remoteUploads = managerUploads.filter((u) => u.uploadType === 'remote');
        if (remoteUploads.length > 0) {
          const s3Base = generateS3Url();
          const existingMobileVideoUids = new Set(
            prev.filter((u) => u.isMobileUpload && !u.isPhotoAlbum).map((u) => u.mobileUid),
          );

          // Refresh thumbnail/s3 cache from current state and manager (survives auto-removal)
          prev.forEach((u) => {
            if (u.isMobileUpload && !u.isPhotoAlbum && u.fileName) {
              const cached = mobileVideoCacheRef.current[u.fileName] || {};
              if (u.thumbnail) cached.thumbnail = u.thumbnail;
              if (u.s3Url) cached.s3Url = u.s3Url;
              if (u.s3Key) cached.s3Key = u.s3Key;
              mobileVideoCacheRef.current[u.fileName] = cached;
            }
          });
          remoteUploads.forEach((ru) => {
            const ext = `.${(ru.fileName || '').split('.').pop().toLowerCase()}`;
            if (!IMAGE_EXTENSIONS.includes(ext) && ru.fileName) {
              const cached = mobileVideoCacheRef.current[ru.fileName] || {};
              if (ru.thumbnail) cached.thumbnail = ru.thumbnail;
              if (ru.s3Key) { cached.s3Key = ru.s3Key; cached.s3Url = `${s3Base}${ru.s3Key}`; }
              mobileVideoCacheRef.current[ru.fileName] = cached;
            }
          });

          const newVideoEntries = [];
          let hasNewMobilePhotos = false;

          remoteUploads.forEach((u) => {
            const ext = `.${(u.fileName || '').split('.').pop().toLowerCase()}`;
            const isImage = IMAGE_EXTENSIONS.includes(ext);
            if (isImage) {
              if (!trackedMobilePhotoUidsRef.current.has(u.id)) {
                trackedMobilePhotoUidsRef.current.add(u.id);
                hasNewMobilePhotos = true;
              }
            } else if (!existingMobileVideoUids.has(u.id)) {
              const s3Url = u.s3Key ? `${s3Base}${u.s3Key}` : null;
              const cached = mobileVideoCacheRef.current[u.fileName] || {};
              newVideoEntries.push({
                id: `mobile-${u.id}`,
                mobileUid: u.id,
                fileName: u.fileName,
                fileSize: u.totalBytes || 0,
                status: u.status === 'completed' ? UPLOAD_STATUS.COMPLETED : UPLOAD_STATUS.UPLOADING,
                progress: u.progress || 0,
                bytesUploaded: u.bytesUploaded || 0,
                speed: u.speed || 0,
                timeRemaining: u.timeRemaining || 0,
                thumbnail: u.thumbnail || cached.thumbnail || null,
                s3Url: s3Url || cached.s3Url || null,
                s3Key: u.s3Key || cached.s3Key || null,
                type: 'video',
                isMobileUpload: true,
              });
            }
          });

          // Aggregate mobile photos into a single mobile photo album entry
          const totalMobilePhotos = trackedMobilePhotoUidsRef.current.size;
          if (totalMobilePhotos > 0) {
            const activeMobilePhotos = Array.from(trackedMobilePhotoUidsRef.current).filter((uid) => {
              const mu = managerUploads.find((m) => m.id === uid);
              return mu && mu.status !== 'completed';
            }).length;
            const completedMobilePhotos = totalMobilePhotos - activeMobilePhotos;
            const allMobileDone = activeMobilePhotos === 0;
            const mobileStatus = allMobileDone ? UPLOAD_STATUS.COMPLETED : UPLOAD_STATUS.UPLOADING;

            // Build photosPreviews from mobile thumbnails (matches existing album rendering shape)
            const photosPreviews = Array.from(trackedMobilePhotoUidsRef.current).map((uid) => {
              const mu = managerUploads.find((m) => m.id === uid);
              const previewUrl = mu?.thumbnail || (mu?.s3Key ? `${s3Base}${mu.s3Key}` : null);
              return { name: mu?.fileName || uid, previewUrl };
            });

            const existingMobileAlbum = updated.find((u) => u.isMobileUpload && u.isPhotoAlbum);
            if (existingMobileAlbum) {
              if (existingMobileAlbum.totalPhotos !== totalMobilePhotos
                || existingMobileAlbum.uploadedPhotos !== completedMobilePhotos
                || existingMobileAlbum.status !== mobileStatus) {
                updated = updated.map((u) => (u.id === existingMobileAlbum.id ? {
                  ...u,
                  totalPhotos: totalMobilePhotos,
                  uploadedPhotos: completedMobilePhotos,
                  fileName: `${totalMobilePhotos} photo${totalMobilePhotos > 1 ? 's' : ''}`,
                  status: mobileStatus,
                  progress: mobileStatus === UPLOAD_STATUS.COMPLETED ? 100 : u.progress,
                  photosPreviews,
                } : u));
                changed = true;
              }
            } else if (hasNewMobilePhotos) {
              updated = [{
                id: `mobile-photo-album-${Date.now()}`,
                fileName: `${totalMobilePhotos} photo${totalMobilePhotos > 1 ? 's' : ''}`,
                fileSize: 0,
                type: 'album',
                status: mobileStatus,
                progress: mobileStatus === UPLOAD_STATUS.COMPLETED ? 100 : 0,
                bytesUploaded: 0,
                speed: 0,
                timeRemaining: 0,
                isMobileUpload: true,
                isPhotoAlbum: true,
                totalPhotos: totalMobilePhotos,
                uploadedPhotos: completedMobilePhotos,
                photosPreviews,
              }, ...updated];
              changed = true;
            }
          }

          if (newVideoEntries.length > 0 || hasNewMobilePhotos) {
            changed = true;
            // Close QR modal once files start arriving from mobile
            setShowMobileQr(false);
            setShowUploadSection(false);
            setContentTabType('all');
            setIsUploading(true);
            setShowPlaceholderFolder(true);
            updated = [...newVideoEntries, ...updated];
          }
        }

        return changed ? updated : prev;
      });

      // Check if all uploads done
      const allDone = managerUploads.every(
        (u) => u.status === 's3_complete' || u.status === 'completed' || u.status === 'failed' || u.status === 'cancelled',
      );
      if (allDone && uploadsRef.current.length > 0) {
        const hasActive = uploadsRef.current.some(
          (u) => u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PENDING,
        );
        if (!hasActive) {
          setIsUploading(false);
        }
      }
    });
    return () => unsubscribe();
  }, [fetchContent]);

  // Handle file selection
  // Lazily create/fetch the ordering gate for recorded-clip index `i`.
  // Each gate is a promise that resolves once that clip has finished
  // registering; the next clip awaits the previous index before it starts.
  const getRecordedGate = useCallback((i) => {
    const m = recordedGatesRef.current;
    if (!m.has(i)) {
      let resolve;
      const promise = new Promise((r) => { resolve = r; });
      m.set(i, { promise, resolve });
    }
    return m.get(i);
  }, []);

  // Start video upload using uploadLargeFile (normal token-based flow)
  const startVideoUpload = useCallback(async (upload) => {
    const { file, id } = upload;
    const draftId = id;
    const videoTitle = file.name.replace(/\.[^/.]+$/, '');

    // Generate thumbnails BEFORE upload starts (same as gift-flow)
    let thumbnailBase64 = null;
    let allThumbnailsBase64 = [];

    // Recorded clips (from GiftRecorder) carry a real captured frame on the
    // File itself. Re-decoding their MediaRecorder WebM blob on Safari yields a
    // black/failed frame and would overwrite the good one with the default
    // placeholder — so reuse it directly and skip regeneration, exactly like
    // the sender flow (gift-flow).
    if (file.thumbnail) {
      thumbnailBase64 = file.thumbnail;
      allThumbnailsBase64 = [file.thumbnail];
    } else {
      // Step 1: Client-side generation from local file
      try {
        const videoUrl = URL.createObjectURL(file);
        const thumbResult = await generateThumbnailsFromUrl(videoUrl);
        URL.revokeObjectURL(videoUrl);
        const [firstThumb] = thumbResult.thumbnails || [];
        if (firstThumb && firstThumb !== image04) {
          thumbnailBase64 = firstThumb;
          allThumbnailsBase64 = thumbResult.thumbnails;
        }
      } catch (thumbErr) {
        // Client thumbnail generation failed
      }
    }

    // Step 2: Backend API fallback if client failed
    if (!thumbnailBase64) {
      try {
        const videoFilePiece = new FormData();
        videoFilePiece.append('video_file', file.slice(0, 5 * 1024 * 1024));
        const backendResp = await fetch('https://uploader.projector.app/generate-thumbnail/', {
          method: 'POST',
          body: videoFilePiece,
        });
        if (backendResp.ok) {
          const backendData = await backendResp.json();
          if (backendData?.thumbnail) {
            thumbnailBase64 = `data:image/png;base64,${backendData.thumbnail}`;
            allThumbnailsBase64 = [thumbnailBase64];
          }
        }
      } catch (backendErr) {
        // Backend API fallback failed
      }
    }

    // Update upload item with generated thumbnail for display (same as gift-flow setSelectedFiles posterUrl).
    // Never clobber an already-good poster (e.g. the recorder's captured frame) with the placeholder.
    const posterUrl = thumbnailBase64 || upload.posterUrl || image04;
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, posterUrl } : u)));

    // Store thumbnail data for use in onSuccess
    videoMetadataRef.current.set(draftId, { videoTitle, file, thumbnailBase64, allThumbnailsBase64 });

    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: UPLOAD_STATUS.UPLOADING } : u)));

    return new Promise((resolve) => {
      uploadLargeFile({
        userId: giftUid || 'user',
        file,
        draftId,
        thumbnails: [],
        suppressToast: true,
        onProgress: (progress) => {
          const progressPercent = Math.round(progress * 0.9);
          setUploads((prev) => prev.map((u) => {
            if (u.id !== id) return u;
            return { ...u, progress: Math.max(progressPercent, u.progress || 0), status: UPLOAD_STATUS.UPLOADING };
          }));
        },
        onSuccess: async (successDraftId, data) => {
          // Recorded clips register strictly in recording order: hold this
          // clip's registration until the previous recorded clip has finished
          // (its S3 may have completed in any order). Non-recorded uploads
          // (recordOrder === null) skip the gate and register immediately.
          const ord = (typeof upload.recordOrder === 'number') ? upload.recordOrder : null;
          if (ord !== null && ord > 0) {
            try { await getRecordedGate(ord - 1).promise; } catch (e) { /* noop */ }
          } else if (ord === null && lastRecordedIndexRef.current >= 0) {
            // Non-recorded upload: hold until the LAST recorded clip has
            // registered (its gate resolves only after the whole recorded
            // chain), so recorded answers always land ahead of picked files.
            try { await getRecordedGate(lastRecordedIndexRef.current).promise; } catch (e) { /* noop */ }
          }
          console.log('[GIFT_CONTENT] ===== UPLOAD onSuccess START =====');
          console.log('[GIFT_CONTENT] draftId:', successDraftId);
          console.log('[GIFT_CONTENT] s3Key:', data?.s3Key);
          console.log('[GIFT_CONTENT] documentId:', data?.documentId);
          console.log('[GIFT_CONTENT] token available:', !!getToken());
          console.log('[GIFT_CONTENT] uploader_token available:', !!localStorage.getItem('uploader_token'));
          console.log('[GIFT_CONTENT] isExistingUser:', isExistingUser);
          setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, isRegistering: true } : u)));
          try {
            const documentId = data?.documentId;

            // Use pre-generated thumbnails from before upload (same as gift-flow)
            const metadata = videoMetadataRef.current.get(draftId);
            const storedBase64 = metadata?.thumbnailBase64;
            const storedAll = metadata?.allThumbnailsBase64 || [];
            let thumbBlob = null;
            const thumbsList = [];

            if (storedBase64) {
              thumbBlob = base64ToBinary(storedBase64);
              storedAll.forEach((t) => { thumbsList.push(base64ToBinary(t)); });
            } else {
              // All methods failed — use default image (image04) same as gift-flow
              try {
                const fallbackResp = await fetch(image04);
                const fallbackBlob = await fallbackResp.blob();
                const fallbackBase64 = await new Promise((res) => {
                  const reader = new FileReader();
                  reader.onload = () => res(reader.result);
                  reader.readAsDataURL(fallbackBlob);
                });
                thumbBlob = base64ToBinary(fallbackBase64);
                thumbsList.push(thumbBlob);
              } catch (fallbackErr) {
                // Failed to fetch fallback image
              }
            }

            const formData = new FormData();
            formData.append('draft_id', successDraftId);
            formData.append('title', videoTitle);
            formData.append('category_id', '');
            const currentName = receiverNameRef.current;
            const defaultCategoryName = currentName && currentName !== 'Your' ? `${currentName.trim()}'s videos` : 'All Videos';
            // Receiver-recorder uploads carry a `categoryNameOverride` so each
            // section's clips land in its own category (e.g. "Beginning").
            const videoCategoryName = upload.categoryNameOverride || defaultCategoryName;
            console.log('[GIFT_CONTENT] Video category_name:', videoCategoryName, '| receiverName:', currentName, '| override:', upload.categoryNameOverride);
            formData.append('category_name', videoCategoryName);
            formData.append('file_name', file.name);
            formData.append('file_key', data?.s3Key || '');
            formData.append('document_id', documentId || '');
            formData.append('bucket_folder_path', data?.s3Key || '');
            if (thumbBlob) {
              formData.append('thumbnail', thumbBlob);
            } else {
              formData.append('thumbnail', '');
            }
            thumbsList.forEach((t) => {
              formData.append('thumbnails[]', t);
            });
            formData.append('description', '');
            formData.append('video_size', changeSize(file.size));
            formData.append('group_ids', JSON.stringify([]));
            formData.append('visibility', '1');
            formData.append('s3_status', 'completed');

            if (isExistingUser) {
              // Existing user: store action for later execution
              console.log('[GIFT_CONTENT] Existing user — storing addVideo as pending gift action');
              const actionPayload = {};
              formData.forEach((value, key) => {
                if (!(value instanceof Blob)) {
                  actionPayload[key] = value;
                }
              });
              await addGiftAction({
                gift_uuid: giftUid,
                user_id: localStorage.getItem('giftUserId') || null,
                action: {
                  action_id: `vid_${draftId}`,
                  action_type: 'addVideo',
                  endpoint: '/V6/addVideo',
                  method: 'POST',
                  payload: actionPayload,
                },
              });
              console.log('[GIFT_CONTENT] addGiftAction completed for:', videoTitle);
              // Immediately refetch so the new pending video appears in the list
              fetchPendingActions().catch((refetchErr) => {
                console.warn('[GIFT_CONTENT] Refetch after addVideo action failed:', refetchErr);
              });
            } else {
              // New user: call addVideo directly
              console.log('[GIFT_CONTENT] Step 1: Calling /V6/addVideo...');
              const addVideoResponse = await apiAxios.post(`${baseUrl}/V6/addVideo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });

              // Parse response — backend may return HTML error + JSON as raw string
              let addVideoData = addVideoResponse?.data;
              if (typeof addVideoData === 'string') {
                try {
                  const jsonMatch = addVideoData.match(/\{[\s\S]*\}$/);
                  if (jsonMatch) {
                    addVideoData = JSON.parse(jsonMatch[0]);
                  }
                } catch (parseErr) {
                  console.error('[GIFT_CONTENT] Failed to parse addVideo response string:', parseErr);
                }
              }
              console.log('[GIFT_CONTENT] Step 1 DONE — addVideo parsed:', { success: addVideoData?.success, draft_id: addVideoData?.draft_id, video_id: addVideoData?.video_id });

              // Step 2: Link draft to document
              const responseDraftId = addVideoData?.draft_id;
              console.log('[GIFT_CONTENT] Step 2: updateDraftLink — documentId:', documentId, 'draft_id:', responseDraftId);
              if (documentId && responseDraftId) {
                try {
                  await updateDraftLink({ document_id: documentId, draft_id: responseDraftId });
                  console.log('[GIFT_CONTENT] Step 2 DONE — updateDraftLink succeeded');
                } catch (linkErr) {
                  console.error('[GIFT_CONTENT] Step 2 FAILED — updateDraftLink error:', linkErr);
                }
              } else {
                console.warn('[GIFT_CONTENT] Step 2 SKIPPED — missing documentId:', documentId, 'or draft_id:', responseDraftId);
              }
            }

            console.log('[GIFT_CONTENT] ===== Video fully registered:', videoTitle, '=====');
            setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100, isRegistering: false } : u)));
          } catch (err) {
            console.error('[GIFT_CONTENT] ===== UPLOAD FAILED =====', err);
            setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: UPLOAD_STATUS.FAILED, isRegistering: false } : u)));
          } finally {
            // Always release the gate so later recorded clips never deadlock,
            // even if this one's registration threw.
            if (ord !== null) getRecordedGate(ord).resolve();
          }
          resolve();
        },
        onError: (error) => {
          console.error('[GIFT_CONTENT] Upload error:', error);
          // S3 upload itself failed → onSuccess won't fire, so release the
          // gate here to unblock subsequent recorded clips.
          const ord = (typeof upload.recordOrder === 'number') ? upload.recordOrder : null;
          if (ord !== null) getRecordedGate(ord).resolve();
          setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: UPLOAD_STATUS.FAILED, isRegistering: false } : u)));
          resolve();
        },
      });
    });
  }, [giftUid, fetchPendingActions, getRecordedGate]);

  // Start album upload - upload each photo to S3, then call createAndAttachAlbum
  // Start album upload — use gift upload path (createGiftUploadSession) so photos go to gifts/ S3 prefix
  // that imagekit can serve (same as gift flow step 3)
  const startAlbumUpload = useCallback(async (upload) => {
    const { files: imageFiles, id } = upload;

    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: UPLOAD_STATUS.UPLOADING } : u)));

    const currentName = receiverNameRef.current;
    const albumTitle = currentName && currentName !== 'Your' ? `${currentName.trim()}'s Album` : 'All photos';
    console.log('[GIFT_CONTENT] Album title:', albumTitle, '| receiverName:', currentName, '| isExistingUser:', isExistingUser);

    if (isExistingUser) {
      // ===== EXISTING USER: Upload to S3 via gift sessions, store action for later =====
      const photoS3Data = [];
      let totalUploaded = 0;
      const totalSize = imageFiles.reduce((sum, f) => sum + f.size, 0);

      for (let i = 0; i < imageFiles.length; i += 1) {
        const photoFile = imageFiles[i];
        try {
          // eslint-disable-next-line no-await-in-loop
          const session = await createGiftUploadSession(giftUid, {
            file_name: photoFile.name,
            file_size: photoFile.size,
            content_type: photoFile.type || 'image/jpeg',
          });

          const presignedUrl = session.presigned_urls?.[0]?.presigned_url;
          if (!presignedUrl) {
            console.error('[GIFT_CONTENT] No presigned URL for photo:', photoFile.name);
            // eslint-disable-next-line no-continue
            continue;
          }

          const uploadedSoFar = totalUploaded;
          // eslint-disable-next-line no-await-in-loop
          const partResult = await uploadPartWithPresignedUrl(
            presignedUrl,
            photoFile,
            1,
            (loaded, total) => {
              const photoProgress = loaded / total;
              const overallUploaded = uploadedSoFar + (photoFile.size * photoProgress);
              const progressPercent = Math.round((overallUploaded / totalSize) * 90);
              setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, progress: progressPercent } : u)));
            },
          );

          // eslint-disable-next-line no-await-in-loop
          const completeResponse = await completeGiftUploadSession(session.id, giftUid, [partResult]);
          const s3Key = completeResponse?.s3_key || completeResponse?.file_key || session.s3_key || session.file_key;

          photoS3Data.push({ path: s3Key, size: String(photoFile.size), name: photoFile.name });
          totalUploaded += photoFile.size;
          console.log('[GIFT_CONTENT] Photo uploaded to S3:', photoFile.name, 'key:', s3Key);
        } catch (err) {
          console.error('[GIFT_CONTENT] Photo upload failed:', photoFile.name, err);
        }
      }

      if (photoS3Data.length === 0) {
        setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: UPLOAD_STATUS.FAILED } : u)));
        return;
      }

      setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, isRegistering: true } : u)));
      try {
        const albumPayload = {
          title: albumTitle,
          description: '',
          visibility: '2',
          group_id: '',
          icon: photoS3Data[0]?.path || '',
          photos: photoS3Data,
        };

        console.log('[GIFT_CONTENT] Existing user — storing createAndAttachAlbum as pending gift action');
        await addGiftAction({
          gift_uuid: giftUid,
          user_id: localStorage.getItem('giftUserId') || null,
          action: {
            action_id: `album_${id}`,
            action_type: 'createAndAttachAlbum',
            endpoint: '/createAndAttachAlbum',
            method: 'POST',
            payload: albumPayload,
          },
        });
        console.log('[GIFT_CONTENT] Album action stored with', photoS3Data.length, 'photos');
        // Immediately refetch so the new pending album appears in the list
        fetchPendingActions().catch((refetchErr) => {
          console.warn('[GIFT_CONTENT] Refetch after createAndAttachAlbum action failed:', refetchErr);
        });
        setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100, isRegistering: false } : u)));
      } catch (err) {
        console.error('[GIFT_CONTENT] Failed to store album action:', err);
        setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: UPLOAD_STATUS.FAILED, isRegistering: false } : u)));
      }
    } else {
      // ===== NEW USER: Use main app method — addEditAlbum + addPhotoContent with token =====
      try {
        const token = getToken();
        console.log('[GIFT_CONTENT] New user album upload — token:', token ? 'present' : 'MISSING');

        // Step 1: Check if album already exists
        let existingAlbumId = null;
        try {
          const albumListResponse = await apiAxios.post(`${baseUrl}/getAlbumList`, {});
          const existingAlbums = albumListResponse.data?.data || [];
          if (existingAlbums.length > 0 && currentName && currentName !== 'Your') {
            const match = existingAlbums.find((a) => a.title && a.title.startsWith(currentName.trim()));
            if (match) {
              existingAlbumId = match.album_id || match.id;
              console.log('[GIFT_CONTENT] Found existing album:', existingAlbumId, 'title:', match.title);
            }
          }
        } catch (listErr) {
          console.warn('[GIFT_CONTENT] Failed to fetch album list:', listErr);
        }

        // Step 2: Create album if not existing
        let albumId = existingAlbumId;
        if (!albumId) {
          console.log('[GIFT_CONTENT] Creating new album:', albumTitle);
          const createResp = await apiAxios.post(`${baseUrl}/addEditAlbum`, {
            title: albumTitle,
            description: '',
            visibility: '2',
            group_id: '',
            token,
          });
          albumId = createResp.data?.album_id || createResp.data?.data?.album_id;
          console.log('[GIFT_CONTENT] Album created:', albumId, 'response:', createResp?.data);
        }

        if (!albumId) {
          console.error('[GIFT_CONTENT] No album ID — cannot upload photos');
          setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: UPLOAD_STATUS.FAILED } : u)));
          return;
        }

        // Step 3: Upload photos one by one via /addPhotoContent
        let totalUploaded = 0;
        const totalSize = imageFiles.reduce((sum, f) => sum + f.size, 0);

        for (let i = 0; i < imageFiles.length; i += 1) {
          const photoFile = imageFiles[i];
          try {
            const formData = new FormData();
            formData.append('album_id', albumId);
            formData.append('photo_file[]', photoFile);
            formData.append('visibility', '2');
            formData.append('token', token);
            // Set icon from first photo for new albums
            if (i === 0 && !existingAlbumId) {
              formData.append('icon', photoFile);
            }

            const uploadedSoFar = totalUploaded;
            // eslint-disable-next-line no-await-in-loop, no-loop-func
            await apiAxios.post(`${baseUrl}/addPhotoContent`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (progressEvent) => {
                const photoProgress = progressEvent.loaded / progressEvent.total;
                const overallUploaded = uploadedSoFar + (photoFile.size * photoProgress);
                const progressPercent = Math.round((overallUploaded / totalSize) * 90);
                setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, progress: Math.max(progressPercent, u.progress || 0) } : u)));
              },
            });

            totalUploaded += photoFile.size;
            console.log('[GIFT_CONTENT] Photo uploaded:', photoFile.name, `(${i + 1}/${imageFiles.length})`);
          } catch (photoErr) {
            console.error('[GIFT_CONTENT] Photo upload failed:', photoFile.name, photoErr);
            console.error('[GIFT_CONTENT] Error details:', photoErr?.response?.data || photoErr?.message);
          }
        }

        console.log('[GIFT_CONTENT] Album upload complete — albumId:', albumId, 'photos:', imageFiles.length);
        setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 } : u)));
      } catch (err) {
        console.error('[GIFT_CONTENT] Album upload failed:', err);
        console.error('[GIFT_CONTENT] Error details:', err?.response?.data || err?.message);
        setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: UPLOAD_STATUS.FAILED } : u)));
      }
    }
  }, [giftUid, isExistingUser, fetchPendingActions]);

  // Start all uploads (fire all — upload manager handles concurrency)
  const startUploads = useCallback(async (newUploads) => {
    const promises = newUploads.map((upload) => {
      if (upload.type === 'video') return startVideoUpload(upload);
      if (upload.type === 'album') return startAlbumUpload(upload);
      return Promise.resolve();
    });
    await Promise.allSettled(promises);
    setIsUploading(false);
  }, [startVideoUpload, startAlbumUpload]);

  // Handle file selection from file picker.
  // Optional `options.categoryNameOverride` lets callers (e.g. the receiver
  // recorder) force every produced upload into a specific category, instead
  // of the default "<ReceiverName>'s videos" bucket used at upload time.
  const handleFileSelect = useCallback((event, options = {}) => {
    const { categoryNameOverride = null } = options;
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const videoFiles = [];
    const imageFiles = [];

    files.forEach((file) => {
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      if (VIDEO_EXTENSIONS.includes(ext)) videoFiles.push(file);
      else if (IMAGE_EXTENSIONS.includes(ext)) imageFiles.push(file);
    });

    if (videoFiles.length === 0 && imageFiles.length === 0) {
      setSnackbar({ isVisible: true, message: 'Please select valid video or image files', type: 'error' });
      return;
    }

    const newUploads = videoFiles.map((file) => {
      const draftId = `gc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      // Generate thumbnail preview from video file
      let thumbnail = null;
      try {
        thumbnail = URL.createObjectURL(file);
      } catch (e) { /* ignore */ }
      return {
        id: draftId,
        secureUploadId: draftId,
        fileName: file.name,
        fileSize: file.size,
        file,
        type: 'video',
        status: UPLOAD_STATUS.PENDING,
        progress: 0,
        bytesUploaded: 0,
        speed: 0,
        timeRemaining: 0,
        previewUrl: thumbnail,
        // Real captured frame attached by GiftRecorder (receiver-flow recorder).
        // Showing it as the poster immediately avoids an iOS-Safari black <video>
        // preview during the pending/upload window; startVideoUpload later
        // overwrites posterUrl with the regenerated 6-frame thumbnail.
        posterUrl: file.thumbnail || null,
        categoryNameOverride,
        // Recording-order index stamped by handleReceiverRecordingComplete on
        // recorded clips; null for picked/cloud uploads (which aren't ordered).
        recordOrder: (typeof file.giftRecordOrder === 'number') ? file.giftRecordOrder : null,
      };
    });

    if (imageFiles.length > 0) {
      const albumDraftId = `gc-album-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      // Generate preview URLs for album photos
      const photosPreviews = imageFiles.slice(0, 9).map((f) => {
        try { return { name: f.name, previewUrl: URL.createObjectURL(f) }; } catch (e) { return { name: f.name, previewUrl: null }; }
      });
      newUploads.push({
        id: albumDraftId,
        secureUploadId: null,
        fileName: `${imageFiles.length} photo${imageFiles.length > 1 ? 's' : ''}`,
        fileSize: imageFiles.reduce((sum, f) => sum + f.size, 0),
        files: imageFiles,
        type: 'album',
        status: UPLOAD_STATUS.PENDING,
        progress: 0,
        bytesUploaded: 0,
        speed: 0,
        timeRemaining: 0,
        photosPreviews,
      });
    }

    setUploads((prev) => [...prev, ...newUploads]);
    setShowUploadSection(false);
    setContentTabType('all');
    setIsUploading(true);
    setShowPlaceholderFolder(true);

    startUploads(newUploads);

    if (event.target) {
      const inputElement = event.target;
      setTimeout(() => { inputElement.value = ''; }, 0);
    }
  }, [startUploads]);

  // Receiver recorder finished — fires once at the end of the unified
  // session. `meta.fileSections` is a parallel array of section titles
  // (or null for custom-added/unsectioned clips) provided by GiftRecorder
  // when it was mounted with the `sections` prop. We group files by section
  // and fan out to handleFileSelect once per group so each group's clips
  // land in the right category in the receiver's library.
  const handleReceiverRecordingComplete = (filesOrFile, meta) => {
    closeReceiverRecorder();
    const arr = Array.isArray(filesOrFile) ? filesOrFile : [filesOrFile];
    const files = arr.filter(Boolean);
    if (!files.length) return;

    // Stamp a monotonic recording-order index on each clip BEFORE grouping by
    // section, so order is preserved across sections. startVideoUpload reads
    // it (via the upload's recordOrder) to register clips in this exact order.
    files.forEach((file) => {
      try {
        // eslint-disable-next-line no-param-reassign
        file.giftRecordOrder = recordedSeqRef.current;
        recordedSeqRef.current += 1;
      } catch (e) { /* noop — expando assignment unsupported (very rare) */ }
    });
    // Remember the highest recorded index so non-recorded uploads can wait
    // behind the whole recorded batch before registering.
    lastRecordedIndexRef.current = recordedSeqRef.current - 1;

    const fileSections = (meta && Array.isArray(meta.fileSections)) ? meta.fileSections : [];
    const groups = new Map();
    files.forEach((file, i) => {
      const category = fileSections[i] || 'Unsectioned';
      if (!groups.has(category)) groups.set(category, []);
      groups.get(category).push(file);
    });

    groups.forEach((groupFiles, category) => {
      handleFileSelect({ target: { files: groupFiles } }, { categoryNameOverride: category });
    });
  };

  // Open cloud connect modal (mirrors gift-flow handleCloudConnect)
  const handleCloudConnect = useCallback((service) => {
    sessionStorage.setItem('giftContentCloudConnect', service);
    sessionStorage.setItem('oauthReturnUrl', '/gift-content');
    setSelectedCloudService(service);
    setCloudModalOpen(true);
  }, []);

  const handleCloudModalClose = useCallback(() => {
    setCloudModalOpen(false);
    setSelectedCloudService(null);
  }, []);

  // Mobile upload — open QR popup, create SSE session, render scannable URL.
  // Mirrors QuickUserUploadPage.handleOpenMobileUpload. The actual SSE consumption
  // and remote-upload tracking happens in app.jsx (listens for the
  // 'mobileUploadSessionCreated' event and routes events into uploadManagerSecure).
  const handleOpenMobileUpload = useCallback(async () => {
    setShowMobileQr(true);
    setMobileQrLoading(true);
    setMobileQrError(null);
    setMobileShortUrl('');

    let id = null;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await createUploadSSESession();
        id = response?.data?.session_id;
        if (id) break;
      } catch (err) {
        console.warn(`[GiftContent] Session creation attempt ${attempt} failed:`, err);
        if (attempt < 3) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 1000));
        }
      }
    }

    if (!id) {
      setMobileQrError('Failed to create session. Please try again.');
      setMobileQrLoading(false);
      return;
    }

    setMobileSessionId(id);
    localStorage.setItem('mobileUploadSessionId', id);
    window.dispatchEvent(new CustomEvent('mobileUploadSessionCreated', { detail: { sessionId: id } }));

    const token = localStorage.getItem('token') || '';
    const uploaderToken = localStorage.getItem('uploader_token') || '';
    // Pass receiver name so the mobile page can register uploads under the right
    // category/album (e.g. "Shyam's videos" / "Shyam's Album").
    const receiver = (receiverNameRef.current || '').trim();
    const receiverParam = receiver && receiver !== 'Your' ? `&receiver=${encodeURIComponent(receiver)}` : '';
    // For existing users, route mobile uploads through the gift-action staging
    // pipeline (matches gift-content's local upload behavior so the recipient's
    // Next click can attach them to the gift).
    const giftUidStr = (giftUid || '').trim();
    const giftUserIdStr = (localStorage.getItem('giftUserId') || '').trim();
    const existingParam = isExistingUser
      ? `&isExisting=1${giftUidStr ? `&giftUid=${encodeURIComponent(giftUidStr)}` : ''}${giftUserIdStr ? `&giftUserId=${encodeURIComponent(giftUserIdStr)}` : ''}`
      : '';
    const path = `mobile-upload?session=${id}${token ? `&token=${token}` : ''}${uploaderToken ? `&access=${uploaderToken}` : ''}${receiverParam}${existingParam}`;
    try {
      const shortRes = await createShortLink({ token, path });
      if (shortRes?.data?.link) {
        setMobileShortUrl(shortRes.data.link);
      }
    } catch (shortErr) {
      console.warn('[GiftContent] Short link failed, using full URL:', shortErr);
    }

    setMobileQrLoading(false);
  }, []);

  const handleCloseMobileQr = useCallback(() => {
    setShowMobileQr(false);
    setMobileSessionId(null);
    setMobileQrError(null);
    setMobileShortUrl('');
  }, []);

  const mobileQrUrl = mobileShortUrl
    || (mobileSessionId
      ? (() => {
        const r = (receiverName || '').trim();
        const rParam = r && r !== 'Your' ? `&receiver=${encodeURIComponent(r)}` : '';
        const gUid = (giftUid || '').trim();
        const gUserId = (localStorage.getItem('giftUserId') || '').trim();
        const eParam = isExistingUser
          ? `&isExisting=1${gUid ? `&giftUid=${encodeURIComponent(gUid)}` : ''}${gUserId ? `&giftUserId=${encodeURIComponent(gUserId)}` : ''}`
          : '';
        return `${window.location.origin}/mobile-upload?session=${mobileSessionId}${localStorage.getItem('token') ? `&token=${localStorage.getItem('token')}` : ''}${localStorage.getItem('uploader_token') ? `&access=${localStorage.getItem('uploader_token')}` : ''}${rParam}${eParam}`;
      })()
      : '');

  // Download a Dropbox / Google Drive file as a File object (mirrors gift-flow downloadCloudFile)
  const downloadCloudFile = useCallback(async (cloudFile, source) => {
    const cloudServices = localStorage.getItem('cloudConnectedServices');
    const cloudAccessToken = cloudServices ? JSON.parse(cloudServices)[source]?.accessToken : null;
    if (!cloudAccessToken) {
      throw new Error(`${source === 'dropbox' ? 'Dropbox' : 'Google Drive'} not connected`);
    }

    let tempUrl = null;
    const headers = {};

    if (source === 'dropbox') {
      const dropboxHeaders = {
        Authorization: `Bearer ${cloudAccessToken}`,
        'Content-Type': 'application/json',
      };
      if (cloudFile.namespaceId) {
        dropboxHeaders['Dropbox-API-Path-Root'] = JSON.stringify({
          '.tag': 'namespace_id',
          namespace_id: cloudFile.namespaceId,
        });
      }
      const response = await axios.post(
        'https://api.dropboxapi.com/2/files/get_temporary_link',
        { path: cloudFile.id },
        { headers: dropboxHeaders },
      );
      tempUrl = response.data.link;
    } else if (source === 'googledrive') {
      tempUrl = `https://www.googleapis.com/drive/v3/files/${cloudFile.id}?alt=media`;
      headers.Authorization = `Bearer ${cloudAccessToken}`;
    }

    const downloadResponse = await fetch(tempUrl, { headers });
    if (!downloadResponse.ok) {
      throw new Error(`Failed to download ${cloudFile.name}`);
    }
    const blob = await downloadResponse.blob();
    let mimeType = cloudFile.mimeType || blob.type || '';
    if (!mimeType || mimeType === 'application/octet-stream') {
      const ext = (cloudFile.name || '').split('.').pop().toLowerCase();
      const mimeMap = {
        mp4: 'video/mp4',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        mkv: 'video/x-matroska',
        wmv: 'video/x-ms-wmv',
        flv: 'video/x-flv',
        webm: 'video/webm',
        m4v: 'video/x-m4v',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        heic: 'image/heic',
        heif: 'image/heif',
      };
      mimeType = mimeMap[ext] || 'application/octet-stream';
    }
    return new File([blob], cloudFile.name, { type: mimeType });
  }, []);

  // Process cloud files: download then upload (mirrors QuickUserUploadPage processCloudFiles)
  const processCloudFiles = useCallback(async (files, source) => {
    const videoFiles = [];
    const imageFiles = [];
    files.forEach((file) => {
      const isVideo = file.fileType === 'video' || file.mimeType?.startsWith('video/');
      if (isVideo) videoFiles.push(file);
      else imageFiles.push(file);
    });

    // Flip into upload view immediately so placeholders are visible
    setShowUploadSection(false);
    setContentTabType('all');
    setIsUploading(true);
    setShowPlaceholderFolder(true);

    // Add placeholder entries so user sees progress while downloading
    const placeholderIds = {};
    videoFiles.forEach((cloudFile) => {
      const placeholderId = `cloud-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      placeholderIds[cloudFile.id || cloudFile.name] = placeholderId;
      setUploads((prev) => [...prev, {
        id: placeholderId,
        secureUploadId: placeholderId,
        fileName: cloudFile.name,
        fileSize: cloudFile.size || 0,
        file: null,
        type: 'video',
        status: UPLOAD_STATUS.PENDING,
        progress: 0,
        bytesUploaded: 0,
        speed: 0,
        timeRemaining: 0,
        previewUrl: null,
        isCloudDownloading: true,
      }]);
    });

    if (imageFiles.length > 0) {
      const photoPlaceholderId = `cloud-album-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      placeholderIds.photoAlbumKey = photoPlaceholderId;
      const totalPhotoSize = imageFiles.reduce((sum, f) => sum + (f.size || 0), 0);
      setUploads((prev) => [...prev, {
        id: photoPlaceholderId,
        secureUploadId: null,
        fileName: `${imageFiles.length} photo${imageFiles.length > 1 ? 's' : ''}`,
        fileSize: totalPhotoSize,
        files: [],
        type: 'album',
        status: UPLOAD_STATUS.PENDING,
        progress: 0,
        bytesUploaded: 0,
        speed: 0,
        timeRemaining: 0,
        photosPreviews: [],
        isCloudDownloading: true,
      }]);
    }

    // Download videos sequentially, then trigger upload for each
    await videoFiles.reduce(async (prev, cloudFile) => {
      await prev;
      const placeholderId = placeholderIds[cloudFile.id || cloudFile.name];
      try {
        const localFile = await downloadCloudFile(cloudFile, source);
        let thumbnail = null;
        try { thumbnail = URL.createObjectURL(localFile); } catch (e) { /* ignore */ }
        const videoUpload = {
          id: placeholderId,
          secureUploadId: placeholderId,
          fileName: localFile.name,
          fileSize: localFile.size,
          file: localFile,
          type: 'video',
          status: UPLOAD_STATUS.PENDING,
          progress: 0,
          bytesUploaded: 0,
          speed: 0,
          timeRemaining: 0,
          previewUrl: thumbnail,
        };
        setUploads((prevUploads) => prevUploads.map((u) => (u.id === placeholderId ? videoUpload : u)));
        startVideoUpload(videoUpload);
      } catch (error) {
        console.error('[GIFT_CONTENT] Cloud video download failed:', cloudFile.name, error);
        setUploads((prevUploads) => prevUploads.map((u) => (u.id === placeholderId
          ? { ...u, status: UPLOAD_STATUS.FAILED, isCloudDownloading: false }
          : u)));
        setSnackbar({
          isVisible: true,
          message: `Failed to download ${cloudFile.name}`,
          type: 'error',
        });
      }
    }, Promise.resolve());

    // Download photos sequentially, then upload as album
    if (imageFiles.length > 0) {
      const photoPlaceholderId = placeholderIds.photoAlbumKey;
      try {
        const localImages = await imageFiles.reduce(async (prev, cloudFile) => {
          const acc = await prev;
          const file = await downloadCloudFile(cloudFile, source);
          acc.push(file);
          return acc;
        }, Promise.resolve([]));

        if (localImages.length > 0) {
          const photosPreviews = localImages.slice(0, 9).map((f) => {
            try { return { name: f.name, previewUrl: URL.createObjectURL(f) }; } catch (e) { return { name: f.name, previewUrl: null }; }
          });
          const albumUpload = {
            id: photoPlaceholderId,
            secureUploadId: null,
            fileName: `${localImages.length} photo${localImages.length > 1 ? 's' : ''}`,
            fileSize: localImages.reduce((sum, f) => sum + f.size, 0),
            files: localImages,
            type: 'album',
            status: UPLOAD_STATUS.PENDING,
            progress: 0,
            bytesUploaded: 0,
            speed: 0,
            timeRemaining: 0,
            photosPreviews,
          };
          setUploads((prevUploads) => prevUploads.map((u) => (u.id === photoPlaceholderId ? albumUpload : u)));
          await startAlbumUpload(albumUpload);
        }
      } catch (error) {
        console.error('[GIFT_CONTENT] Cloud photo download failed:', error);
        setUploads((prevUploads) => prevUploads.map((u) => (u.id === photoPlaceholderId
          ? { ...u, status: UPLOAD_STATUS.FAILED, isCloudDownloading: false }
          : u)));
        setSnackbar({
          isVisible: true,
          message: 'Failed to download photos',
          type: 'error',
        });
      }
    }

    setIsUploading(false);
  }, [downloadCloudFile, startVideoUpload, startAlbumUpload]);

  // After files are picked from cloud, close modal and process
  const handleCloudFilesSelected = useCallback((files, source) => {
    if (!files || files.length === 0) return;
    handleCloudModalClose();
    processCloudFiles(files, source);
  }, [handleCloudModalClose, processCloudFiles]);

  // Pause/Resume/Cancel handlers
  const handlePauseUpload = useCallback((upload) => {
    if (upload.secureUploadId) {
      uploadManagerSecure.pauseUpload(upload.secureUploadId);
    }
  }, []);

  const handleResumeUpload = useCallback((upload) => {
    if (upload.secureUploadId) {
      uploadManagerSecure.resumeUpload(upload.secureUploadId);
    }
  }, []);

  const handleCancelUpload = useCallback((upload) => {
    if (upload.secureUploadId) {
      uploadManagerSecure.cancelUpload(upload.secureUploadId);
    }
    // cancelUpload fires neither onSuccess nor onError, so release this
    // recorded clip's ordering gate here — otherwise later recorded clips
    // would wait forever on a clip that's never going to register.
    if (typeof upload.recordOrder === 'number') {
      getRecordedGate(upload.recordOrder).resolve();
    }
    setUploads((prev) => prev.map((u) => (u.id === upload.id ? { ...u, status: UPLOAD_STATUS.CANCELLED } : u)));
  }, [getRecordedGate]);

  const handleUploadDone = useCallback(() => {
    setUploads([]);
    setIsUploading(false);
    setContentTabType('videos');
    // Refresh content list after uploads
    setContentLoading(true);
    if (isExistingUser && giftUid) {
      fetchPendingActions().finally(() => setContentLoading(false));
    } else if (getToken()) {
      fetchContent().finally(() => setContentLoading(false));
    } else {
      setContentLoading(false);
    }
  }, [isExistingUser, giftUid, fetchPendingActions, fetchContent]);

  return (
    <div className={styles.giftContentPage}>
      <CloudUploadModal
        open={cloudModalOpen}
        onClose={handleCloudModalClose}
        service={selectedCloudService}
        onFilesSelected={handleCloudFilesSelected}
        isUploading={false}
        onShowMessage={(message) => {
          setSnackbar({ isVisible: true, message, type: 'error' });
        }}
      />

      {/* Mobile Upload QR Popup (mirrors QuickUserUploadPage) */}
      {showMobileQr && (
        <div
          className={styles.qrOverlay}
          onClick={handleCloseMobileQr}
          onKeyDown={(e) => { if (e.key === 'Escape') handleCloseMobileQr(); }}
          role="button"
          tabIndex={0}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div className={styles.qrModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className={styles.qrModalHeader}>
              <div className={styles.qrModalHeaderLeft}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 12 }}>
                  <rect x="5" y="1" width="14" height="22" rx="3" stroke="#5aa5ef" strokeWidth="1.5" />
                  <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="#5aa5ef" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="12" cy="19" r="1" fill="#5aa5ef" />
                </svg>
                <h2 className={styles.qrModalTitle}>Scan to Upload</h2>
              </div>
              <button className={styles.qrModalCloseBtn} onClick={handleCloseMobileQr} type="button">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
                </svg>
              </button>
            </div>
            <div className={styles.qrModalContent}>
              {mobileQrLoading && (
                <div className={styles.qrModalCentered}>
                  <div className={styles.qrSpinner} />
                  <p className={styles.qrModalHint}>Creating session...</p>
                </div>
              )}
              {mobileQrError && (
                <div className={styles.qrModalCentered}>
                  <p className={styles.qrModalError}>{mobileQrError}</p>
                  <button type="button" className={styles.qrRetryBtn} onClick={handleOpenMobileUpload}>
                    Try Again
                  </button>
                </div>
              )}
              {!mobileQrLoading && !mobileQrError && mobileSessionId && (
                <>
                  <div className={styles.qrModalQrWrapper}>
                    <QRCodeComponent url={mobileQrUrl} size={200} />
                  </div>
                  <p className={styles.qrModalHint}>Scan with your phone camera to upload files from your mobile device</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <Snackbar
        isVisible={snackbar.isVisible}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar({ ...snackbar, isVisible: false })}
      />

      {/* Video Preview Modal */}
      {previewOpen && (
        <div role="button" tabIndex={0} className={styles.previewOverlay} onClick={closePreview} onKeyDown={(e) => { if (e.key === 'Escape') closePreview(); }}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div role="dialog" aria-modal="true" className={styles.previewModal} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <button type="button" className={styles.previewClose} onClick={closePreview}>
              &times;
            </button>
            <div className={styles.previewPlayer}>
              {previewLoading ? (
                <div className={styles.previewLoading}>
                  <div className={styles.loadingSpinner} />
                  Loading preview...
                </div>
              ) : previewVideo?.videoUrl ? (
                <NetflixPlayer
                  src={previewVideo.videoUrl}
                  poster={previewVideo.thumbnail || undefined}
                  fillHeight
                />
              ) : (
                <div className={styles.previewLoading}>
                  <img
                    src={previewVideo?.thumbnail || image04}
                    alt={previewVideo?.title}
                    className={styles.previewFallbackImg}
                  />
                  <p>Video preview not available</p>
                </div>
              )}
            </div>
            {previewVideo?.title && (
              <div className={styles.previewTitle}>{previewVideo.title}</div>
            )}
          </div>
        </div>
      )}

      {/* Album Photos Modal */}
      {albumOpen && (
        <div role="button" tabIndex={0} className={styles.previewOverlay} onClick={closeAlbum} onKeyDown={(e) => { if (e.key === 'Escape') closeAlbum(); }}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div role="dialog" aria-modal="true" className={styles.albumModal} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <button type="button" className={styles.previewClose} onClick={closeAlbum}>
              &times;
            </button>
            {albumDetail?.title && (
              <div className={styles.albumModalHeader}>{albumDetail.title}</div>
            )}
            <div className={styles.albumPhotosContainer}>
              {albumLoading ? (
                <div className={styles.previewLoading}>
                  <div className={styles.loadingSpinner} />
                  Loading photos...
                </div>
              ) : albumDetail?.photos?.length > 0 ? (
                <div className={styles.albumPhotosGrid}>
                  {albumDetail.photos.map((photo, index) => (
                    <div key={photo.id || index} className={styles.albumPhotoItem}>
                      <img
                        src={photo.imagekit_url || photo.photo_file || photo.url || image01}
                        alt={photo.title || `Photo ${index + 1}`}
                        onError={(e) => { e.target.src = image01; }}
                      />
                      <button
                        type="button"
                        className={styles.albumPhotoDelete}
                        onClick={() => {
                          const photoData = albumDetail?.photos?.find((p) => String(p.id) === String(photo.id));
                          if (photoData) {
                            setAlbumDetail((prev) => {
                              if (!prev) return prev;
                              return { ...prev, photos: prev.photos.filter((p) => String(p.id) !== String(photo.id)) };
                            });
                            setUserAlbums((prev) => prev.map((a) => {
                              if (String(a.id) === String(albumDetail.albumId)) {
                                const updatedPhotos = (a.photos || []).filter((p) => String(p.id) !== String(photo.id));
                                return { ...a, photoCount: (a.photoCount || 1) - 1, photos: updatedPhotos };
                              }
                              return a;
                            }));
                            setDeletedItems((prev) => [...prev, {
                              type: 'photo',
                              id: photoData.id,
                              title: photoData.title || photoData.name || `Photo ${photoData.id}`,
                              thumbnail: photoData.imagekit_url || photoData.photo_file || photoData.url || image01,
                              albumId: albumDetail.albumId,
                              albumTitle: albumDetail?.title || 'Album',
                              photoData,
                            }]);
                            setSnackbar({ isVisible: true, message: 'Moved to Restore tab', type: 'success' });
                          }
                        }}
                        title="Delete photo"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.previewLoading}>
                  <p>No photos in this album</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Back link — when the upload-options section is open, back should
          close it and return to the "Your Media" view rather than navigating
          off the page (e.g. back to avatar settings). */}
      <div
        aria-hidden="true"
        onClick={() => {
          if (showUploadSection) {
            setShowUploadSection(false);
          } else {
            navigate(-1);
          }
        }}
        className={styles.backLink}
      >
        BACK
      </div>

      {/* Header */}
      <div className={styles.header}>
        <div />
        <div className={styles.headerRight} />
      </div>

      {/* Hidden file input for uploads */}
      <input
        type="file"
        ref={filesInputRef}
        style={{ display: 'none' }}
        accept={FILE_ACCEPT}
        multiple
        onChange={handleFileSelect}
      />

      {/* Upload Options Section - replaces entire content area */}
      {showUploadSection && (
        <div className={styles.contentWrapper}>
          <div className={styles.uploadOptionsWrapper}>
            <h1 className={styles.uploadOptionsTitle}>Choose Content Source</h1>
            <p className={styles.uploadOptionsSubtitle}>
              Select where to upload your content from
            </p>
            {/* Three explicit rows (3 + 2 + 2) — mirrors the sender's
                Add Media grid for visual consistency. Row 1 has the
                dominant actions (Local · Mobile · Record), rows 2/3
                are compact cloud-source tiles. */}
            <div className={styles.uploadOptionsGrid}>

              {/* ── Row 1 ── */}
              <div className={styles.uploadOptionsRow}>
                {/* Choose Local Files */}
                <div
                  aria-hidden="true"
                  className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay1}`}
                  onClick={() => filesInputRef.current?.click()}
                >
                  <div className={styles.uploadOptionIcon}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className={styles.uploadOptionText}>Choose Local Files</span>
                </div>
                {/* Upload From Mobile - hidden on iOS web and small screens */}
                {!isIOSWeb && (
                  <div
                    aria-hidden="true"
                    className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay2} ${styles.hideOnMobile}`}
                    onClick={handleOpenMobileUpload}
                  >
                    <div className={styles.uploadOptionIcon}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="5" y="1" width="14" height="22" rx="3" stroke="white" strokeWidth="1.5" />
                        <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                        <circle cx="12" cy="19" r="1" fill="white" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Upload From Mobile</span>
                  </div>
                )}
                {/* Record Yourself */}
                {VIDEO_RECORDER_ENABLED && (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Record yourself"
                    className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay3}`}
                    onClick={() => { setShowUploadSection(false); setShowRecordPromptScreen(true); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowUploadSection(false); setShowRecordPromptScreen(true); } }}
                  >
                    <div className={styles.uploadOptionIcon}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="2" y="6" width="14" height="12" rx="2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M22 8L16 12L22 16V8Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="9" cy="12" r="2.6" fill="#ff4d4f" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Record Yourself</span>
                  </div>
                )}
              </div>

              {/* ── Row 2 ── */}
              <div className={`${styles.uploadOptionsRow} ${styles.uploadOptionsRowCompact}`}>
                {/* Apple Photos - hidden on iOS web */}
                {!isIOSWeb && (
                  <div
                    aria-hidden="true"
                    className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay3} ${styles.hideOnMobile}`}
                    onClick={handleOpenMobileUpload}
                  >
                    <div className={styles.uploadOptionIcon}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#999" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Apple Photos</span>
                  </div>
                )}
                {/* Google Photos */}
                <div
                  aria-hidden="true"
                  className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay1}`}
                  onClick={() => handleCloudConnect('googlephotos')}
                >
                  <div className={styles.uploadOptionIcon}>
                    <svg width="40" height="40" viewBox="0 0 122.88 122.88" xmlns="http://www.w3.org/2000/svg">
                      <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" fill="#F6B704" />
                      <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" fill="#E54335" />
                      <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" fill="#4280EF" />
                      <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" fill="#34A353" />
                    </svg>
                  </div>
                  <span className={styles.uploadOptionText}>Google Photos</span>
                </div>
              </div>

              {/* ── Row 3 ── */}
              <div className={`${styles.uploadOptionsRow} ${styles.uploadOptionsRowCompact}`}>
                {/* Dropbox */}
                <div
                  aria-hidden="true"
                  className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay2}`}
                  onClick={() => handleCloudConnect('dropbox')}
                >
                  <div className={styles.uploadOptionIcon}>
                    <svg width="40" height="40" viewBox="0 0 43 40" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.5 0L0 8.1l8.6 6.9 12.5-7.8L12.5 0zM0 21.9l12.5 8.1 8.6-7.1-12.5-7.8L0 21.9zM21.1 22.9l8.6 7.1 12.5-8.1-8.6-6.8-12.5 7.8zM42.2 8.1L29.7 0l-8.6 7.2 12.5 7.8 8.6-6.9zM21.2 24.6l-8.6 7.1-4-2.6v2.9l12.6 7.6 12.6-7.6v-2.9l-4 2.6-8.6-7.1z" fill="#0061ff" />
                    </svg>
                  </div>
                  <span className={styles.uploadOptionText}>Dropbox</span>
                </div>
                {/* Google Drive */}
                <div
                  aria-hidden="true"
                  className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay3}`}
                  onClick={() => handleCloudConnect('googledrive')}
                >
                  <div className={styles.uploadOptionIcon}>
                    <svg width="32" height="32" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA" />
                      <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47" />
                      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335" />
                      <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.35c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
                      <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.55c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC" />
                      <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.5c0-1.55-.4-3.1-1.2-4.5l-12.75-22z" fill="#FFBA00" />
                    </svg>
                  </div>
                  <span className={styles.uploadOptionText}>Google Drive</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              className={styles.uploadBackButton}
              onClick={() => setShowUploadSection(false)}
            >
              Back to Content
            </button>
          </div>
        </div>
      )}

      {/* Content Area - hidden when upload section is active */}
      {!showUploadSection && (
      <div className={styles.contentWrapper}>
        {/* Gift message popup — controlled by per-folder pill clicks. Mounts
            once with the currently-open folder's clips. Gated on
            VIDEO_RECORDER_ENABLED so the whole gift-message feature is
            silent when the recorder is off. */}
        {VIDEO_RECORDER_ENABLED && openGiftEntry && (
          <GiftMessagePlayer
            open
            onClose={handleCloseFolderGift}
            clips={openGiftEntry.clips}
            recordedCount={openGiftEntry.recordedCount}
            fromName={openGiftEntry.fromName || giftFromName}
          />
        )}

        {/* Prompt: ask the receiver if they'd like to record themselves too.
            Dismissable; opt-in. Recorded clips upload via handleFileSelect →
            same path as gallery picks, landing in the receiver's own folder. */}
        <div className={styles.pageTitleRow}>
          <div>
            <h1 className={styles.pageTitle}>Your Media</h1>
            <p style={{ color: '#aaa', fontSize: '0.95rem', fontFamily: "'Montserrat', sans-serif", fontWeight: 500, margin: '-18px 0 16px 0', textAlign: 'left' }}>
              Choose what you want to keep or get rid of!
            </p>
          </div>
        </div>

        <div className={styles.contentTabContent}>
          {/* Content Type Tabs */}
          <div className={styles.contentTypeTabsRow}>
            {selectedCategory && (
              <button
                type="button"
                className={styles.tabBackButton}
                onClick={() => { setSelectedCategory(null); setFolderTab('all'); }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 12H5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 19L5 12L12 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <div className={styles.contentTypeTabs}>
              {selectedCategory ? (
                <>
                  <button
                    className={`${styles.contentTypeTab} ${folderTab === 'all' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setFolderTab('all')}
                  >
                    All
                  </button>
                  <button
                    className={`${styles.contentTypeTab} ${folderTab === 'videos' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setFolderTab('videos')}
                  >
                    Videos
                  </button>
                  <button
                    className={`${styles.contentTypeTab} ${folderTab === 'photos' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setFolderTab('photos')}
                  >
                    Photo Albums
                  </button>
                  {deletedItems.length > 0 && (
                    <button
                      className={`${styles.contentTypeTab} ${styles.restoreTab} ${contentTabType === 'restore' ? styles.contentTypeTabActive : ''}`}
                      onClick={() => { setSelectedCategory(null); setContentTabType('restore'); }}
                    >
                      Restore
                      <span className={styles.restoreBadge}>{deletedItems.length}</span>
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    className={`${styles.contentTypeTab} ${contentTabType === 'videos' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setContentTabType('videos')}
                  >
                    Folders
                  </button>
                  <button
                    className={`${styles.contentTypeTab} ${contentTabType === 'allVideos' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setContentTabType('allVideos')}
                  >
                    Videos
                  </button>
                  <button
                    className={`${styles.contentTypeTab} ${contentTabType === 'photos' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setContentTabType('photos')}
                  >
                    Photo Albums
                  </button>
                  {uploads.length > 0 && (
                    <button
                      className={`${styles.contentTypeTab} ${contentTabType === 'all' ? styles.contentTypeTabActive : ''}`}
                      onClick={() => setContentTabType('all')}
                    >
                      Uploads
                    </button>
                  )}
                  {deletedItems.length > 0 && (
                    <button
                      className={`${styles.contentTypeTab} ${styles.restoreTab} ${contentTabType === 'restore' ? styles.contentTypeTabActive : ''}`}
                      onClick={() => setContentTabType('restore')}
                    >
                      Restore
                      <span className={styles.restoreBadge}>{deletedItems.length}</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Action Bar */}
          <div className={styles.contentActionBar}>
            <span className={styles.contentCount}>
              {contentTabType === 'videos' && (
                selectedCategory
                  ? (folderTab === 'videos' ? `${filteredVideos.length} Videos`
                    : folderTab === 'photos' ? `${filteredAlbums.length} Albums`
                      : `${filteredVideos.length + filteredAlbums.length} Items`)
                  : `${categories.length} ${categories.length === 1 ? 'Folder' : 'Folders'}`
              )}
              {contentTabType === 'allVideos' && `${userVideos.length} Videos`}
              {contentTabType === 'photos' && `${userAlbums.length} Albums`}
              {contentTabType === 'restore' && `${deletedItems.length} Items`}
            </span>
          </div>

          {/* Loading State */}
          {contentLoading && (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner} />
              Loading content...
            </div>
          )}

          {/* Content Grid */}
          {!contentLoading && (
            <div className={styles.contentGrid}>
              {contentTabType === 'videos' ? (
                (userVideos.length > 0 || userAlbums.length > 0) ? (
                  selectedCategory ? (
                    /* Inside folder: All / Videos / Photo Albums tabs */
                    <>
                      {(folderTab === 'all' || folderTab === 'videos') && filteredVideos.map((video) => (
                        <div key={video.id} className={styles.contentCard}>
                          <div className={styles.contentCardThumbnail}>
                            {isExistingUser && video.isPending && video.thumbFailed && video.videoUrl ? (
                              <video src={video.videoUrl} poster={image04} preload="metadata" muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <img src={video.thumbnail} alt={video.title} onError={(e) => { e.target.src = image04; }} />
                            )}
                            {video.duration && <span className={styles.videoDuration}>{video.duration}</span>}
                            <button type="button" className={styles.videoPlayOverlay} onClick={() => handleVideoClick(video)} title="Play Video">
                              <span className={styles.videoPlayIcon}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="#000" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </span>
                            </button>
                            <button type="button" className={styles.deleteCardButton} onClick={(e) => handleSoftDelete('video', video, e)} title="Delete">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                          <div className={styles.contentCardInfo}>
                            <div className={styles.contentCardTitleRow}>
                              <h4 className={styles.contentCardTitle}>{video.title}</h4>
                              <span className={styles.contentTypeIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="23 7 16 12 23 17 23 7" />
                                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {/* Shimmer placeholders for videos currently uploading */}
                      {(folderTab === 'all' || folderTab === 'videos') && selectedCategory === uploadingCategory && activeVideoUploads.map((upload) => (
                        <div key={`shimmer-${upload.id}`} className={`${styles.contentCard} ${styles.uploadingCard}`}>
                          <div className={styles.contentCardThumbnail}>
                            {upload.posterUrl ? (
                              <img src={upload.posterUrl} alt={upload.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.src = image04; }} />
                            ) : upload.previewUrl ? (
                              // eslint-disable-next-line jsx-a11y/media-has-caption
                              <video src={upload.previewUrl} muted playsInline preload="metadata" />
                            ) : (
                              <div className={styles.uploadingCardPlaceholder}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
                              </div>
                            )}
                            {upload.status !== UPLOAD_STATUS.COMPLETED && (
                              <>
                                <div className={styles.uploadingCardShimmer} />
                                <div className={styles.uploadingCardProgress}>
                                  {upload.progress > 0 ? (
                                    <div className={styles.uploadingCardProgressBar} style={{ width: `${upload.progress}%` }} />
                                  ) : (
                                    <div className={styles.uploadingCardProgressIndeterminate} />
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          <div className={styles.contentCardInfo}>
                            <div className={styles.contentCardTitleRow}>
                              <h4 className={styles.contentCardTitle}>{upload.fileName.length > 18 ? `${upload.fileName.substring(0, 18)}...` : upload.fileName}</h4>
                              <span className={styles.contentTypeIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="23 7 16 12 23 17 23 7" />
                                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(folderTab === 'all' || folderTab === 'photos') && filteredAlbums.map((album) => {
                        const isAlbumUploading = activeAlbumUploads.length > 0 && selectedCategory === uploadingCategory;
                        const activeUpload = isAlbumUploading ? activeAlbumUploads[0] : null;
                        return (
                        <div key={`album-${album.id}`} className={`${styles.contentCard}${isAlbumUploading ? ` ${styles.uploadingCard}` : ''}`}>
                          <div className={styles.contentCardThumbnail}>
                            {album.photos && album.photos.length > 0 ? (
                              <div className={getAlbumGridClass(album.photos.length)}>
                                {album.photos.slice(0, (album.photoCount || album.photos.length) > 9 ? 8 : 9).map((photo, idx) => (
                                  <div key={photo.id || idx}>
                                    {photo.url ? (
                                      <img src={photo.url} alt={photo.name || `Photo ${idx + 1}`} onError={(e) => { e.target.src = image01; }} />
                                    ) : null}
                                  </div>
                                ))}
                                {(album.photoCount || album.photos.length) > 9 && (
                                  <div className={styles.albumFolderGridCount}>{`+${(album.photoCount || album.photos.length) - 8}`}</div>
                                )}
                              </div>
                            ) : (
                              <img src={album.thumbnail} alt={album.title} onError={(e) => { e.target.src = image01; }} />
                            )}
                            {isAlbumUploading && activeUpload && activeUpload.status !== UPLOAD_STATUS.COMPLETED && (
                              <>
                                <div className={styles.uploadingCardShimmer} />
                                <div className={styles.uploadingCardProgress}>
                                  {activeUpload.progress > 0 ? (
                                    <div className={styles.uploadingCardProgressBar} style={{ width: `${activeUpload.progress}%` }} />
                                  ) : (
                                    <div className={styles.uploadingCardProgressIndeterminate} />
                                  )}
                                </div>
                              </>
                            )}
                            {!isAlbumUploading && (
                            <button type="button" className={styles.previewEyeButton} onClick={() => handleAlbumClick(album)} title="Preview">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
                              </svg>
                            </button>
                            )}
                            {!isAlbumUploading && (
                            <button type="button" className={styles.deleteCardButton} onClick={(e) => handleSoftDelete('album', album, e)} title="Delete">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                            )}
                          </div>
                          <div className={styles.contentCardInfo}>
                            <div className={styles.contentCardTitleRow}>
                              <h4 className={styles.contentCardTitle}>{album.title}</h4>
                              <span className={styles.contentTypeIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                  <circle cx="8.5" cy="8.5" r="1.5" />
                                  <polyline points="21 15 16 10 5 21" />
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                      {/* Shimmer placeholders for albums currently uploading — only when no existing album */}
                      {(folderTab === 'all' || folderTab === 'photos') && selectedCategory === uploadingCategory && filteredAlbums.length === 0 && activeAlbumUploads.map((upload) => (
                        <div key={`shimmer-${upload.id}`} className={`${styles.contentCard} ${styles.uploadingCard}`}>
                          <div className={styles.contentCardThumbnail}>
                            {upload.photosPreviews && upload.photosPreviews.length > 0 ? (
                              <div className={getAlbumGridClass(upload.photosPreviews.length)}>
                                {upload.photosPreviews.slice(0, upload.photosPreviews.length > 9 ? 8 : 9).map((photo, idx) => (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <div key={idx}>
                                    {photo.previewUrl ? (
                                      <img src={photo.previewUrl} alt={photo.name || `Photo ${idx + 1}`} />
                                    ) : null}
                                  </div>
                                ))}
                                {upload.photosPreviews.length > 9 && (
                                  <div className={styles.albumFolderGridCount}>{`+${upload.photosPreviews.length - 8}`}</div>
                                )}
                              </div>
                            ) : (
                              <div className={styles.uploadingCardPlaceholder}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                              </div>
                            )}
                            {upload.status !== UPLOAD_STATUS.COMPLETED && (
                              <>
                                <div className={styles.uploadingCardShimmer} />
                                <div className={styles.uploadingCardProgress}>
                                  {upload.progress > 0 ? (
                                    <div className={styles.uploadingCardProgressBar} style={{ width: `${upload.progress}%` }} />
                                  ) : (
                                    <div className={styles.uploadingCardProgressIndeterminate} />
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          <div className={styles.contentCardInfo}>
                            <div className={styles.contentCardTitleRow}>
                              <h4 className={styles.contentCardTitle}>{upload.fileName.length > 18 ? `${upload.fileName.substring(0, 18)}...` : upload.fileName}</h4>
                              <span className={styles.contentTypeIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                  <circle cx="8.5" cy="8.5" r="1.5" />
                                  <polyline points="21 15 16 10 5 21" />
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {folderTab === 'videos' && filteredVideos.length === 0 && activeVideoUploads.length === 0 && (
                        <div className={styles.emptyState}><p>No videos</p></div>
                      )}
                      {folderTab === 'photos' && filteredAlbums.length === 0 && activeAlbumUploads.length === 0 && (
                        <div className={styles.emptyState}><p>No photo albums</p></div>
                      )}
                      {folderTab === 'all' && filteredVideos.length === 0 && filteredAlbums.length === 0 && activeUploads.length === 0 && (
                        <div className={styles.emptyState}><p>No content</p></div>
                      )}
                    </>
                  ) : (
                    /* Category folders view */
                    categories.map((cat) => {
                      const receiverLower = (receiverName || '').trim().toLowerCase();
                      const isFolderUploading = cat.isUploading || (activeUploads.length > 0 && receiverLower && receiverLower !== 'your' && cat.name.toLowerCase().startsWith(receiverLower));
                      const giftEntry = giftClipsByFolder.get(cat.name);
                      return (
                        <div
                          key={cat.name}
                          className={`${styles.categoryFolder} ${isFolderUploading ? styles.categoryFolderDisabled : ''}`}
                          role="button"
                          tabIndex={isFolderUploading ? -1 : 0}
                          onClick={() => { if (!isFolderUploading) setSelectedCategory(cat.name); }}
                          onKeyDown={(e) => { if (!isFolderUploading && (e.key === 'Enter' || e.key === ' ')) setSelectedCategory(cat.name); }}
                        >
                          {isFolderUploading && <span className={styles.categoryFolderUploadingBadge}>Uploading...</span>}
                          {VIDEO_RECORDER_ENABLED && giftEntry && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleOpenFolderGift(cat.name); }}
                              aria-label={`Open gift from ${giftEntry.fromName}`}
                              className="giftFolderPill"
                              style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                zIndex: 5,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px 6px 8px',
                                border: 'none',
                                borderRadius: '999px',
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                fontSize: '0.78rem',
                                fontWeight: 700,
                                color: '#1a0e2e',
                                background: 'linear-gradient(135deg, #ffd166 0%, #ff9f6c 50%, #d6435f 100%)',
                                boxShadow: '0 6px 18px rgba(214, 67, 95, 0.45)',
                                whiteSpace: 'nowrap',
                                maxWidth: 'calc(100% - 20px)',
                              }}
                            >
                              <span aria-hidden="true">🎁</span>
                              {`Open Gift · ${giftEntry.clips.length}`}
                            </button>
                          )}
                          <div className={styles.categoryFolderThumbnail}>
                            {cat.gridThumbnails && cat.gridThumbnails.length > 0 ? (
                              <div className={getAlbumGridClass(cat.gridThumbnails.length)}>
                                {cat.gridThumbnails.slice(0, cat.gridThumbnails.length > 9 ? 8 : 9).map((thumb, idx) => (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <div key={idx}>
                                    {thumb.type === 'video' ? (
                                      <video src={thumb.src} preload="metadata" muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                      <img src={thumb.src} alt="" onError={(e) => { e.target.src = image04; }} />
                                    )}
                                  </div>
                                ))}
                                {cat.gridThumbnails.length > 9 && (
                                  <div className={styles.albumFolderGridCount}>{`+${cat.gridThumbnails.length - 8}`}</div>
                                )}
                              </div>
                            ) : (
                              <img
                                src={cat.thumbnail}
                                alt={cat.name}
                                onError={(e) => { e.target.src = image04; }}
                              />
                            )}
                            <div className={styles.categoryFolderOverlay}>
                              <svg className={styles.categoryFolderIcon} width="32" height="32" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V8C22 6.9 21.1 6 20 6H12L10 4Z" />
                              </svg>
                            </div>
                          </div>
                          <div className={styles.categoryFolderInfo}>
                            <h4 className={styles.categoryFolderName}>{cat.name.replace(/'s\s*videos$/i, '')}</h4>
                            <p style={{ color: '#aaa', fontSize: '0.75rem', fontFamily: "'Montserrat', sans-serif", margin: '4px 0 0 0' }}>
                              {(() => {
                                const parts = [
                                  cat.videoCount > 0 ? `${cat.videoCount} ${cat.videoCount === 1 ? 'Video' : 'Videos'}` : null,
                                  cat.photoCount > 0 ? `${cat.photoCount} ${cat.photoCount === 1 ? 'Photo' : 'Photos'}` : null,
                                ].filter(Boolean);
                                if (parts.length > 0) return parts.join(', ');
                                if (cat.isUploading) return 'Uploading...';
                                return ' ';
                              })()}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )
                ) : (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon}>&#127909;</div>
                    <p>No content yet</p>
                  </div>
                )
              ) : contentTabType === 'allVideos' ? (
                (userVideos.length > 0 || activeVideoUploads.length > 0) ? (
                  <>
                  {userVideos.map((video) => (
                    <div
                      key={video.id}
                      className={styles.contentCard}
                    >
                      <div className={styles.contentCardThumbnail}>
                        {isExistingUser && video.isPending && video.thumbFailed && video.videoUrl ? (
                          <video src={video.videoUrl} poster={image04} preload="metadata" muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <img
                            src={video.thumbnail}
                            alt={video.title}
                            onError={(e) => { e.target.src = image04; }}
                          />
                        )}
                        {video.duration && (
                          <span className={styles.videoDuration}>{video.duration}</span>
                        )}
                        <button
                          type="button"
                          className={styles.videoPlayOverlay}
                          onClick={() => handleVideoClick(video)}
                          title="Play Video"
                        >
                          <span className={styles.videoPlayIcon}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="#000" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </span>
                        </button>
                        <button
                          type="button"
                          className={styles.deleteCardButton}
                          onClick={(e) => handleSoftDelete('video', video, e)}
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                      <div className={styles.contentCardInfo}>
                        <div className={styles.contentCardTitleRow}>
                          <h4 className={styles.contentCardTitle}>{video.title}</h4>
                          <span className={styles.contentTypeIcon}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="23 7 16 12 23 17 23 7" />
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                            </svg>
                          </span>
                        </div>
                        <div className={styles.contentCardMeta}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{video.category}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {activeVideoUploads.map((upload) => (
                    <div key={`shimmer-${upload.id}`} className={`${styles.contentCard} ${styles.uploadingCard}`}>
                      <div className={styles.contentCardThumbnail}>
                        {upload.posterUrl ? (
                          <img src={upload.posterUrl} alt={upload.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.src = image04; }} />
                        ) : upload.previewUrl ? (
                          // eslint-disable-next-line jsx-a11y/media-has-caption
                          <video src={upload.previewUrl} muted playsInline preload="metadata" />
                        ) : (
                          <div className={styles.uploadingCardPlaceholder}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
                          </div>
                        )}
                        {upload.status !== UPLOAD_STATUS.COMPLETED && (
                          <>
                            <div className={styles.uploadingCardShimmer} />
                            <div className={styles.uploadingCardProgress}>
                              {upload.progress > 0 ? (
                                <div className={styles.uploadingCardProgressBar} style={{ width: `${upload.progress}%` }} />
                              ) : (
                                <div className={styles.uploadingCardProgressIndeterminate} />
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <div className={styles.contentCardInfo}>
                        <div className={styles.contentCardTitleRow}>
                          <h4 className={styles.contentCardTitle}>{upload.fileName.length > 18 ? `${upload.fileName.substring(0, 18)}...` : upload.fileName}</h4>
                          <span className={styles.contentTypeIcon}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="23 7 16 12 23 17 23 7" />
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                            </svg>
                          </span>
                        </div>
                        <div className={styles.contentCardMeta}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>Uploading...</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  </>
                ) : (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon}>&#127909;</div>
                    <p>No videos yet</p>
                  </div>
                )
              ) : contentTabType === 'photos' ? (
                (userAlbums.length > 0 || activeAlbumUploads.length > 0) ? (
                  <>
                  {userAlbums.map((album) => {
                    const isAlbumUploading = activeAlbumUploads.length > 0;
                    const activeUpload = isAlbumUploading ? activeAlbumUploads[0] : null;
                    return (
                    <div
                      key={album.id}
                      className={`${styles.contentCard}${isAlbumUploading ? ` ${styles.uploadingCard}` : ''}`}
                    >
                      <div className={styles.contentCardThumbnail}>
                        {album.photos && album.photos.length > 0 ? (
                          <div className={getAlbumGridClass(album.photos.length)}>
                            {album.photos.slice(0, (album.photoCount || album.photos.length) > 9 ? 8 : 9).map((photo, idx) => (
                              <div key={photo.id || idx}>
                                {photo.url ? (
                                  <img src={photo.url} alt={photo.name || `Photo ${idx + 1}`} onError={(e) => { e.target.src = image01; }} />
                                ) : null}
                              </div>
                            ))}
                            {(album.photoCount || album.photos.length) > 9 && (
                              <div className={styles.albumFolderGridCount}>{`+${(album.photoCount || album.photos.length) - 8}`}</div>
                            )}
                          </div>
                        ) : (
                          <img
                            src={album.thumbnail}
                            alt={album.title}
                            onError={(e) => { e.target.src = image01; }}
                          />
                        )}
                        {isAlbumUploading && activeUpload && activeUpload.status !== UPLOAD_STATUS.COMPLETED && (
                          <>
                            <div className={styles.uploadingCardShimmer} />
                            <div className={styles.uploadingCardProgress}>
                              {activeUpload.progress > 0 ? (
                                <div className={styles.uploadingCardProgressBar} style={{ width: `${activeUpload.progress}%` }} />
                              ) : (
                                <div className={styles.uploadingCardProgressIndeterminate} />
                              )}
                            </div>
                          </>
                        )}
                        {!isAlbumUploading && (
                        <button
                          type="button"
                          className={styles.previewEyeButton}
                          onClick={() => handleAlbumClick(album)}
                          title="Preview"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
                          </svg>
                        </button>
                        )}
                        {!isAlbumUploading && (
                        <button
                          type="button"
                          className={styles.deleteCardButton}
                          onClick={(e) => handleSoftDelete('album', album, e)}
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                        )}
                      </div>
                      <div className={styles.contentCardInfo}>
                        <div className={styles.contentCardTitleRow}>
                          <h4 className={styles.contentCardTitle}>{album.title}</h4>
                          <span className={styles.contentTypeIcon}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          </span>
                        </div>
                        <div className={styles.contentCardMeta}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{album.title}</span>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                  {/* Shimmer placeholders — only when no existing album */}
                  {userAlbums.length === 0 && activeAlbumUploads.map((upload) => (
                    <div key={`shimmer-${upload.id}`} className={`${styles.contentCard} ${styles.uploadingCard}`}>
                      <div className={styles.contentCardThumbnail}>
                        {upload.photosPreviews && upload.photosPreviews.length > 0 ? (
                          <div className={getAlbumGridClass(upload.photosPreviews.length)}>
                            {upload.photosPreviews.slice(0, upload.photosPreviews.length > 9 ? 8 : 9).map((photo, idx) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <div key={idx}>
                                {photo.previewUrl ? (
                                  <img src={photo.previewUrl} alt={photo.name || `Photo ${idx + 1}`} />
                                ) : null}
                              </div>
                            ))}
                            {upload.photosPreviews.length > 9 && (
                              <div className={styles.albumFolderGridCount}>{`+${upload.photosPreviews.length - 8}`}</div>
                            )}
                          </div>
                        ) : (
                          <div className={styles.uploadingCardPlaceholder}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                          </div>
                        )}
                        {upload.status !== UPLOAD_STATUS.COMPLETED && (
                          <>
                            <div className={styles.uploadingCardShimmer} />
                            <div className={styles.uploadingCardProgress}>
                              {upload.progress > 0 ? (
                                <div className={styles.uploadingCardProgressBar} style={{ width: `${upload.progress}%` }} />
                              ) : (
                                <div className={styles.uploadingCardProgressIndeterminate} />
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <div className={styles.contentCardInfo}>
                        <div className={styles.contentCardTitleRow}>
                          <h4 className={styles.contentCardTitle}>{upload.fileName.length > 18 ? `${upload.fileName.substring(0, 18)}...` : upload.fileName}</h4>
                          <span className={styles.contentTypeIcon}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          </span>
                        </div>
                        <div className={styles.contentCardMeta}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>Uploading...</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  </>
                ) : (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon}>&#128247;</div>
                    <p>No photo albums yet</p>
                  </div>
                )
              ) : contentTabType === 'all' ? (
                /* All Tab — Upload Progress Cards (same UI as step 3) */
                uploads.length > 0 ? (
                  <>
                    {uploads.map((upload) => {
                      const displayFileName = upload.fileName;
                      const isVideo = upload.type === 'video';
                      return (
                        <div key={upload.id} className={styles.uploadProgressItem}>
                          {/* Thumbnail area — 160px */}
                          {isVideo && upload.posterUrl ? (
                            <img src={upload.posterUrl} alt={upload.fileName} style={{ width: '100%', height: '160px', objectFit: 'cover' }} onError={(e) => { e.target.src = image04; }} />
                          ) : isVideo && upload.previewUrl ? (
                            // eslint-disable-next-line jsx-a11y/media-has-caption
                            <video src={upload.previewUrl} muted playsInline preload="metadata" />
                          ) : isVideo && upload.isMobileUpload && upload.thumbnail ? (
                            <img src={upload.thumbnail} alt={upload.fileName} style={{ width: '100%', height: '160px', objectFit: 'cover' }} onError={(e) => { e.target.src = image04; }} />
                          ) : isVideo && upload.isMobileUpload && upload.s3Url && upload.status === UPLOAD_STATUS.COMPLETED ? (
                            // eslint-disable-next-line jsx-a11y/media-has-caption
                            <video src={upload.s3Url} muted playsInline preload="metadata" />
                          ) : upload.type === 'album' && upload.photosPreviews && upload.photosPreviews.length > 0 ? (
                            <div className={getAlbumGridClass(upload.photosPreviews.length)}>
                              {upload.photosPreviews.slice(0, upload.photosPreviews.length > 9 ? 8 : 9).map((photo, idx) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <div key={idx}>
                                  {photo.previewUrl ? (
                                    <img src={photo.previewUrl} alt={photo.name || `Photo ${idx + 1}`} />
                                  ) : null}
                                </div>
                              ))}
                              {upload.photosPreviews.length > 9 && (
                                <div className={styles.albumFolderGridCount}>{`+${upload.photosPreviews.length - 8}`}</div>
                              )}
                            </div>
                          ) : (
                            <div className={styles.gridPlaceholderThumb}>
                              {isVideo ? (
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
                              ) : (
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                              )}
                            </div>
                          )}
                          {/* Card info area */}
                          <div style={{ padding: '8px 10px' }}>
                            <div className={styles.uploadProgressHeader}>
                              <div className={styles.filteredCardTitleRow}>
                                <span className={styles.uploadFileName}>
                                  {displayFileName.length > 30 ? `${displayFileName.substring(0, 30)}...` : displayFileName}
                                </span>
                              </div>
                              <div className={styles.uploadStatusActions}>
                                <span className={styles.uploadStatus}>
                                  {upload.status === UPLOAD_STATUS.UPLOADING && upload.isMobileUpload && (
                                    <span className={styles.mobileUploadStatus}>
                                      Uploading from Mobile...
                                      <span className={styles.mobileUploadSpinner} />
                                    </span>
                                  )}
                                  {upload.status === UPLOAD_STATUS.UPLOADING && !upload.isMobileUpload && `${upload.progress || 0}%`}
                                  {upload.status === UPLOAD_STATUS.PAUSED && 'Paused'}
                                  {upload.status === UPLOAD_STATUS.NETWORK_ERROR && 'Network Error - Will Resume'}
                                  {upload.status === UPLOAD_STATUS.PENDING && 'Waiting...'}
                                  {upload.status === UPLOAD_STATUS.CANCELLING && 'Cancelling...'}
                                  {upload.status === UPLOAD_STATUS.CANCELLED && 'Cancelled'}
                                  {upload.status === UPLOAD_STATUS.S3_COMPLETE && 'Completing...'}
                                </span>
                                {upload.type === 'video' && !upload.isMobileUpload && (upload.status === UPLOAD_STATUS.UPLOADING || upload.status === UPLOAD_STATUS.PAUSED || upload.status === UPLOAD_STATUS.NETWORK_ERROR) && (
                                  <div className={styles.uploadControls}>
                                    {upload.status === UPLOAD_STATUS.UPLOADING && (
                                      <button
                                        type="button"
                                        className={styles.uploadControlButton}
                                        onClick={() => handlePauseUpload(upload)}
                                        title="Pause upload"
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                        </svg>
                                      </button>
                                    )}
                                    {(upload.status === UPLOAD_STATUS.PAUSED || upload.status === UPLOAD_STATUS.NETWORK_ERROR) && (
                                      <button
                                        type="button"
                                        className={styles.uploadControlButton}
                                        onClick={() => handleResumeUpload(upload)}
                                        title="Resume upload"
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                          <path d="M8 5v14l11-7z" />
                                        </svg>
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className={styles.uploadControlButtonCancel}
                                      onClick={() => handleCancelUpload(upload)}
                                      title="Cancel upload"
                                    >
                                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                      </svg>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className={styles.uploadProgressBarContainer}>
                              <div
                                className={`${styles.uploadProgressBar} ${upload.status === UPLOAD_STATUS.COMPLETED ? styles.uploadProgressComplete : ''} ${upload.status === UPLOAD_STATUS.FAILED ? styles.uploadProgressFailed : ''} ${upload.status === UPLOAD_STATUS.PAUSED ? styles.uploadProgressPaused : ''} ${upload.isMobileUpload && upload.status === UPLOAD_STATUS.UPLOADING ? styles.uploadProgressIndeterminate : ''}`}
                                style={{ width: upload.isMobileUpload && upload.status !== UPLOAD_STATUS.COMPLETED ? '100%' : `${upload.progress || 0}%` }}
                              />
                            </div>
                            <div className={styles.uploadProgressDetails}>
                              {upload.status === UPLOAD_STATUS.UPLOADING && !upload.isMobileUpload && (
                                <span>
                                  {changeSize(upload.fileSize)}
                                  {' MB'}
                                </span>
                              )}
                              {upload.status === UPLOAD_STATUS.UPLOADING && upload.isMobileUpload && (
                                <span>Uploading from mobile</span>
                              )}
                              {upload.status === UPLOAD_STATUS.COMPLETED && (
                                <span className={styles.uploadCompleteText}>
                                  {upload.isMobileUpload ? 'Uploaded from mobile' : 'Complete'}
                                </span>
                              )}
                              {upload.status === UPLOAD_STATUS.FAILED && (
                                <span className={styles.uploadFailedText}>Failed</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className={styles.emptyState}>
                    <p>No uploads</p>
                  </div>
                )
              ) : (
                /* Restore Tab Content */
                deletedItems.length > 0 ? (
                  deletedItems.map((item) => (
                    <div
                      key={`${item.type}-${item.id}`}
                      className={styles.contentCard}
                    >
                      <div className={styles.contentCardThumbnail}>
                        {item.type === 'album' && item.photos && item.photos.length > 0 ? (
                          <div className={getAlbumGridClass(item.photos.length)}>
                            {item.photos.slice(0, (item.photoCount || item.photos.length) > 9 ? 8 : 9).map((photo, idx) => (
                              <div key={photo.id || idx}>
                                {photo.url ? (
                                  <img src={photo.url} alt={photo.name || `Photo ${idx + 1}`} onError={(e) => { e.target.src = image01; }} />
                                ) : null}
                              </div>
                            ))}
                            {(item.photoCount || item.photos.length) > 9 && (
                              <div className={styles.albumFolderGridCount}>{`+${(item.photoCount || item.photos.length) - 8}`}</div>
                            )}
                          </div>
                        ) : isExistingUser && item.type === 'video' && item.thumbFailed && item.videoUrl ? (
                          <video src={item.videoUrl} poster={image04} preload="metadata" muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <img
                            src={item.thumbnail}
                            alt={item.title}
                            onError={(e) => { e.target.src = item.type === 'video' ? image04 : image01; }}
                          />
                        )}
                        <span className={styles.restoreTypeBadge}>
                          {item.type === 'video' ? 'Video' : item.type === 'photo' ? 'Photo' : 'Album'}
                        </span>
                      </div>
                      <div className={styles.contentCardInfo}>
                        <h4 className={styles.contentCardTitle}>{item.title}</h4>
                        <div className={styles.contentCardMeta}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{item.type === 'video' ? item.category : item.type === 'photo' ? `From: ${item.albumTitle}` : item.title}</span>
                        </div>
                        <button
                          type="button"
                          className={styles.restoreCardButton}
                          onClick={(e) => { e.stopPropagation(); handleRestore(item); }}
                          title="Restore"
                        >
                          Restore
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    <p>No items to restore</p>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Bottom centered Upload + Next buttons */}
      <div className={styles.bottomNextContainer}>
        {!showUploadSection && (
          <button
            type="button"
            className={styles.bottomUploadButton}
            onClick={() => { setShowUploadSection(true); setShowUploadOptions(true); }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            Add your media
          </button>
        )}
        {(() => {
          const hasActiveUpload = uploads.some((u) => (
            u.status === UPLOAD_STATUS.PENDING
            || u.status === UPLOAD_STATUS.UPLOADING
            || u.status === UPLOAD_STATUS.PAUSED
            || u.status === UPLOAD_STATUS.NETWORK_ERROR
            || u.status === UPLOAD_STATUS.S3_COMPLETE
            || u.status === UPLOAD_STATUS.CANCELLING
            || u.isRegistering
            || u.isCloudDownloading
          ));
          const isDisabled = executingActions || hasActiveUpload;
          return (
            <button
              type="button"
              className={styles.backButton}
              onClick={handleNextClick}
              disabled={isDisabled}
              style={{ opacity: isDisabled ? 0.6 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
            >
              {executingActions ? 'Processing...' : 'Next'}
            </button>
          );
        })()}
      </div>

      {/* Shared "Add a personal video message" flow — tile hub + per-section
          recorder. Each section's recorded clips are kept client-side until
          the user clicks Finish on the hub, at which point onComplete fires
          once with the flat (files, { fileSections }) shape that the
          existing handleReceiverRecordingComplete already understands. */}
      <PersonalVideoMessageFlow
        open={VIDEO_RECORDER_ENABLED && showRecordPromptScreen}
        onClose={closeReceiverRecorder}
        onBack={() => {
          // Back leaves the flag unset so the next Next click re-opens this
          // "Tell your story" page.
          try { localStorage.removeItem('giftReceiverRecordPromptAsked'); } catch (e) { /* ignore */ }
          closeReceiverRecorder();
        }}
        onSkip={() => {
          // Skip is the only action that retires this page — set the one-time
          // flag so subsequent Next clicks bypass it.
          try { localStorage.setItem('giftReceiverRecordPromptAsked', 'true'); } catch (e) { /* ignore */ }
          closeReceiverRecorder();
        }}
        onComplete={(files, meta) => {
          // Submitting/attaching also retires this page — set the one-time
          // flag so the next Next click skips it and goes to the watch page.
          try { localStorage.setItem('giftReceiverRecordPromptAsked', 'true'); } catch (e) { /* ignore */ }
          // Forward into the gift-content upload pipeline (groups by
          // section title → handleFileSelect with categoryNameOverride per
          // group). Closing the prompt after so the user lands back on the
          // categorized library.
          handleReceiverRecordingComplete(files, meta);
        }}
      />
    </div>
  );
};

export default GiftContent;
